import React, {
    useEffect,
    useState,
    useRef,
    useCallback,
    useMemo,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../supabase";
import { useAuth } from "../../contexts/AuthContext";
import { specialties, roleCategories } from "../../constants/specialties";
import Select from "react-select";
import countryList from "react-select-country-list";
import { State } from "country-state-city";
import {
    ensureListingCreditAvailable,
    NO_LISTING_CREDITS_ERROR,
} from "../../services/listingPurchases";
import JobListingPurchasePanel from "../../components/employer/JobListingPurchasePanel";
import { sendNextStepEmail } from "../../services/emailService";
import useListingCredits from "../../hooks/useListingCredits";

// Language options for dropdown
const languages = [
    "English",
    "Afrikaans",
    "Zulu",
    "Xhosa",
    "French",
    "German",
    "Spanish",
    "Portuguese",
    "Arabic",
    "Mandarin",
    "Hindi",
    "Swahili",
    "Yoruba",
    "Igbo",
    "Hausa",
    "Italian",
    "Dutch",
    "Russian",
    "Japanese",
    "Korean",
    "Turkish",
    "Polish",
    "Greek",
    "Hebrew",
    "Urdu",
    "Bengali",
    "Tamil",
    "Telugu",
    "Marathi",
    "Gujarati",
    "Punjabi",
    "Malayalam",
    "Kannada",
    "Odia",
    "Assamese",
    "Nepali",
    "Sinhala",
    "Thai",
    "Vietnamese",
    "Indonesian",
    "Malay",
    "Tagalog",
    "Filipino",
    "Swedish",
    "Norwegian",
    "Danish",
    "Finnish",
    "Czech",
    "Hungarian",
    "Romanian",
    "Bulgarian",
    "Croatian",
    "Serbian",
    "Slovak",
    "Slovenian",
    "Lithuanian",
    "Latvian",
    "Estonian",
    "Ukrainian",
    "Persian",
    "Pashto",
    "Kurdish",
    "Amharic",
    "Somali",
    "Fulani",
    "Wolof",
    "Kinyarwanda",
    "Luganda",
    "Setswana",
    "Sesotho",
    "Tswana",
    "Venda",
    "Tsonga",
    "Ndebele",
    "Swati",
    "Northern Sotho",
    "Southern Sotho",
];

// Common currencies with codes
const currencies = [
    { code: "ZAR", symbol: "R", name: "South African Rand" },
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "EUR", symbol: "€", name: "Euro" },
    { code: "GBP", symbol: "£", name: "British Pound" },
    { code: "AUD", symbol: "A$", name: "Australian Dollar" },
    { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
    { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
    { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
    { code: "GHS", symbol: "₵", name: "Ghanaian Cedi" },
    { code: "INR", symbol: "₹", name: "Indian Rupee" },
    { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
    { code: "CHF", symbol: "Fr", name: "Swiss Franc" },
];

// Salary periods
const salaryPeriods = [
    { value: "yearly", label: "Yearly" },
    { value: "monthly", label: "Monthly" },
    { value: "weekly", label: "Weekly" },
    { value: "hourly", label: "Hourly" },
];

const NEW_JOB_LEAVE_WARNING_MESSAGE =
    "Are you sure you want to leave? Your work will not be saved if you continue.";

const INITIAL_NEW_JOB_FORM = {
    jobTitle: "",
    specialty: "",
    roleDescription: "",
    preferredCandidate: "",
    dutiesAndResponsibilities: "",
    employmentType: "",
    workPreference: "",
    country: "",
    countryCode: "",
    province: "",
    languageRequirement: [],
    locationOrLicenceRestrictions: null,
    locationOrLicenceRestrictionsText: "",
    requirementsSpecialty: "",
    experienceLevel: "",
    yearsOfExperience: "",
    budgetForRole: "",
    salaryCurrency: "ZAR",
    salaryPeriod: "yearly",
    requiredSkills: [],
    otherPreferences: "",
    qualifications: "",
    experienceRequirements: "",
    salaryRange: "",
    benefits: "",
    screeningQuestions: false,
    driversLicenseRequired: false,
    driversLicenseAnswer: null,
    visaSponsorshipOffered: false,
    visaSponsorshipAnswer: null,
    about: "",
    is_dev_job: false,
};

const EnhancedHiringPipeline = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { credits, refresh: refreshCredits } = useListingCredits();
    const [searchParams] = useSearchParams();
    const jobId = searchParams.get("jobId");
    const ALL_JOBS_VALUE = "all";
    const [applications, setApplications] = useState([]);
    const [jobListings, setJobListings] = useState([]);
    const [selectedJobId, setSelectedJobId] = useState(jobId || ALL_JOBS_VALUE);
    const [selectedJob, setSelectedJob] = useState(null);
    const [favoritedApplications, setFavoritedApplications] = useState(
        new Set()
    );

    // Sync selectedJobId with URL parameter
    useEffect(() => {
        if (jobId) {
            setSelectedJobId(jobId);
        } else {
            setSelectedJobId(ALL_JOBS_VALUE);
        }
    }, [jobId, ALL_JOBS_VALUE]);

    const [draggedApplication, setDraggedApplication] = useState(null);
    const [showPromotionModal, setShowPromotionModal] = useState(false);
    const [selectedPromotionPlan, setSelectedPromotionPlan] = useState("free");
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showNewJobModal, setShowNewJobModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [openStatusMenu, setOpenStatusMenu] = useState(null); // Track which card's status menu is open
    const [savingNewJob, setSavingNewJob] = useState(false);
    const [showStageChangeWarning, setShowStageChangeWarning] = useState(false);
    const [pendingStageChange, setPendingStageChange] = useState(null);
    const [jobFormStep, setJobFormStep] = useState(2); // Step 2 = Job description, Step 3 = Requirements
    const [newJobForm, setNewJobForm] = useState(INITIAL_NEW_JOB_FORM);
    const [isCustomRoleCategory, setIsCustomRoleCategory] = useState(false);
    const [isCustomEmploymentType, setIsCustomEmploymentType] = useState(false);
    const [isCustomWorkPreference, setIsCustomWorkPreference] = useState(false);
    const [isCustomCountry, setIsCustomCountry] = useState(false);
    const [isCustomProvince, setIsCustomProvince] = useState(false);
    const [isCustomRequirementsSpecialty, setIsCustomRequirementsSpecialty] =
        useState(false);
    const [isCustomExperienceLevel, setIsCustomExperienceLevel] =
        useState(false);
    const [isCustomSalaryCurrency, setIsCustomSalaryCurrency] = useState(false);
    const [isCustomSalaryPeriod, setIsCustomSalaryPeriod] = useState(false);

    // Additional state for step 3 requirements
    const [skillInput, setSkillInput] = useState("");
    const [currentQuestion, setCurrentQuestion] = useState("");
    const [currentQuestionType, setCurrentQuestionType] =
        useState("multiple-choice"); // "multiple-choice" or "text"
    const [currentOptions, setCurrentOptions] = useState([""]);
    const [screeningQuestionsList, setScreeningQuestionsList] = useState([]);
    const [driversLicenseAnswer, setDriversLicenseAnswer] = useState(null);
    const [visaSponsorshipAnswer, setVisaSponsorshipAnswer] = useState(null);
    const [showNewJobLeaveWarning, setShowNewJobLeaveWarning] = useState(false);

    // Touch drag state for mobile/tablet
    const [touchDragApp, setTouchDragApp] = useState(null);
    const [touchPosition, setTouchPosition] = useState({ x: 0, y: 0 });
    const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });
    const [isDraggingTouch, setIsDraggingTouch] = useState(false);
    const stageRefs = useRef({});
    const touchPreviewRef = useRef(null);
    const touchThreshold = 10; // pixels to move before starting drag

    // Auto-populate screening questions from onboarding when opening new job modal
    useEffect(() => {
        if (showNewJobModal && user && !newJobForm.screeningQuestions) {
            const onboardingQuestions =
                user?.user_metadata?.employer_profile?.requirements
                    ?.screeningQuestionsList;
            const onboardingEnabled =
                user?.user_metadata?.employer_profile?.requirements
                    ?.screeningQuestions;

            if (
                onboardingEnabled &&
                onboardingQuestions &&
                Array.isArray(onboardingQuestions) &&
                onboardingQuestions.length > 0
            ) {
                setNewJobForm((p) => ({ ...p, screeningQuestions: true }));
                setScreeningQuestionsList(onboardingQuestions);
            }
        }
    }, [showNewJobModal, user]);

    const hasUnsavedNewJobDraft = useMemo(() => {
        if (!showNewJobModal) return false;

        if (JSON.stringify(newJobForm) !== JSON.stringify(INITIAL_NEW_JOB_FORM))
            return true;
        if (
            Array.isArray(screeningQuestionsList) &&
            screeningQuestionsList.length > 0
        )
            return true;
        if (typeof skillInput === "string" && skillInput.trim() !== "")
            return true;
        if (
            typeof currentQuestion === "string" &&
            currentQuestion.trim() !== ""
        )
            return true;
        if (
            Array.isArray(currentOptions) &&
            currentOptions.some((o) => String(o || "").trim() !== "")
        )
            return true;
        if (jobFormStep !== 2) return true;

        return false;
    }, [
        showNewJobModal,
        newJobForm,
        screeningQuestionsList,
        skillInput,
        currentQuestion,
        currentOptions,
        jobFormStep,
    ]);

    useEffect(() => {
        if (!hasUnsavedNewJobDraft) return;

        const handler = (e) => {
            e.preventDefault();
            e.returnValue = NEW_JOB_LEAVE_WARNING_MESSAGE;
            return NEW_JOB_LEAVE_WARNING_MESSAGE;
        };

        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [hasUnsavedNewJobDraft]);

    const attemptCloseNewJobModal = () => {
        if (hasUnsavedNewJobDraft) {
            setShowNewJobLeaveWarning(true);
            return;
        }
        setShowNewJobModal(false);
        setJobFormStep(2);
    };

    const confirmLeaveNewJobModal = () => {
        setShowNewJobLeaveWarning(false);
        setShowNewJobModal(false);
        setJobFormStep(2);
    };

    const dismissNewJobLeaveWarning = () => {
        setShowNewJobLeaveWarning(false);
    };

    const stages = [
        { name: "Applications" },
        { name: "Shortlist" },
        { name: "Interview" },
        { name: "Test" },
        { name: "Hired" },
        { name: "Rejected" },
    ];

    const [openMobileStage, setOpenMobileStage] = useState(
        stages[0]?.name ?? null
    );

    const allJobSpecialties = React.useMemo(() => {
        const specialties = (jobListings || [])
            .map((j) => (j?.specialty || "").trim())
            .filter(Boolean);
        return Array.from(new Set(specialties)).sort((a, b) =>
            a.localeCompare(b)
        );
    }, [jobListings]);

    useEffect(() => {
        const fetchApps = async () => {
            try {
                if (!user?.id) {
                    setApplications([]);
                    return;
                }

                let apps = [];

                if (selectedJobId && selectedJobId !== ALL_JOBS_VALUE) {
                    const { data, error } = await supabase
                        .from("applications")
                        .select("*")
                        .eq("job_listing_id", selectedJobId)
                        .order("application_date", { ascending: false });
                    if (error) throw error;
                    apps = data || [];
                } else {
                    // All Jobs: fetch this employer's job ids, then fetch all apps for those jobs
                    const { data: employerJobs, error: employerJobsError } =
                        await supabase
                            .from("internal_job_listings")
                            .select("id")
                            .eq("employer_id", user.id)
                            .neq("status", "closed");

                    if (employerJobsError) throw employerJobsError;

                    const jobIds = (employerJobs || []).map((j) => j.id);
                    if (jobIds.length === 0) {
                        setApplications([]);
                        return;
                    }

                    const { data: allApps, error: allAppsError } =
                        await supabase
                            .from("applications")
                            .select("*")
                            .in("job_listing_id", jobIds)
                            .order("application_date", { ascending: false });

                    if (allAppsError) throw allAppsError;
                    apps = allApps || [];
                }

                // Fetch candidate profiles for names
                const userIds = Array.from(
                    new Set(apps.map((a) => a.user_id).filter(Boolean))
                );
                let profilesByUserId = {};
                if (userIds.length > 0) {
                    const { data: profiles, error: pErr } = await supabase
                        .from("users_profiles")
                        .select(
                            "profileid, firstname, lastname, email, profile_image_url"
                        )
                        .in("profileid", userIds);
                    if (!pErr && profiles) {
                        profilesByUserId = profiles.reduce((acc, p) => {
                            acc[p.profileid] = p;
                            return acc;
                        }, {});
                    }
                }

                const enriched = apps.map((a) => ({
                    ...a,
                    users_profiles: profilesByUserId[a.user_id] || null,
                }));

                setApplications(enriched);

                // Track favorited applications
                const favorited = new Set(
                    enriched
                        .filter((a) => a.is_favorite === true)
                        .map((a) => a.id)
                );
                setFavoritedApplications(favorited);
            } catch (e) {
                setApplications([]);
            }
        };
        fetchApps();
    }, [selectedJobId, user?.id, ALL_JOBS_VALUE]);

    useEffect(() => {
        const fetchJobs = async () => {
            if (!user?.id) return;
            try {
                const { data, error } = await supabase
                    .from("internal_job_listings")
                    .select("id,title,specialty")
                    .eq("employer_id", user.id)
                    .neq("status", "closed") // Exclude archived jobs
                    .order("created_at", { ascending: false });
                if (error) throw error;
                const jobs = data || [];
                setJobListings(jobs);
            } catch (e) {
                setJobListings([]);
            }
        };
        fetchJobs();
    }, [user]);

    // Fetch selected job details
    useEffect(() => {
        const fetchSelectedJob = async () => {
            if (!selectedJobId || selectedJobId === ALL_JOBS_VALUE) {
                setSelectedJob(null);
                return;
            }
            try {
                const { data, error } = await supabase
                    .from("internal_job_listings")
                    .select("id,title,specialty,status")
                    .eq("id", selectedJobId)
                    .single();
                if (error) throw error;
                // If the job is archived, clear the selection
                if (data && data.status === "closed") {
                    setSelectedJob(null);
                    setSelectedJobId("");
                    navigate("/employer/hiring-pipeline");
                    return;
                }
                setSelectedJob(data);
            } catch (e) {
                setSelectedJob(null);
            }
        };
        fetchSelectedJob();
    }, [selectedJobId]);

    const handleSelectJob = (val) => {
        setSelectedJobId(val);
        if (!val || val === ALL_JOBS_VALUE) {
            navigate(`/employer/hiring-pipeline`);
            return;
        }
        navigate(`/employer/hiring-pipeline?jobId=${val}`);
    };

    const getApplicationsForStage = (stageName) => {
        if (stageName === "Rejected") {
            // Show all applications with rejected status
            return applications.filter((a) => a.status === "rejected");
        } else {
            // For other stages, exclude rejected applications and filter by stage
            return applications.filter((a) => {
                const status = a.status?.toLowerCase();
                return (
                    status !== "rejected" &&
                    (a.stage || "Applications") === stageName
                );
            });
        }
    };

    const applyStageChange = async (
        application,
        targetStageName,
        notifyApplicant = false
    ) => {
        if (!application || !targetStageName) return;

        const statusMap = {
            Applications: "applied",
            Shortlist: "screening",
            Interview: "interview",
            Test: "assessment",
            Hired: "hired",
            Rejected: "rejected",
        };
        const newStatus = statusMap[targetStageName] || "applied";

        try {
            await supabase
                .from("applications")
                .update({
                    stage: targetStageName,
                    status: newStatus,
                    last_updated: new Date().toISOString(),
                })
                .eq("id", application.id);

            setApplications((prev) =>
                prev.map((a) =>
                    a.id === application.id
                        ? { ...a, stage: targetStageName, status: newStatus }
                        : a
                )
            );

            // Send next step email only when employer opted in (same eligibility as before)
            if (
                notifyApplicant &&
                targetStageName !== "Applications" &&
                targetStageName !== "Rejected" &&
                application.stage !== targetStageName
            ) {
                try {
                    const { data: candidateData } = await supabase
                        .from("users_profiles")
                        .select("email, firstname, lastname")
                        .eq("profileid", application.user_id)
                        .single();

                    const { data: jobData } = await supabase
                        .from("internal_job_listings")
                        .select("title, company_name, id")
                        .eq("id", application.job_listing_id)
                        .single();

                    if (candidateData && jobData) {
                        const candidateName =
                            `${candidateData.firstname || ""} ${
                                candidateData.lastname || ""
                            }`.trim() || "Candidate";
                        const jobLink = `https://doctorsjobs.io/job/${jobData.id}`;
                        await sendNextStepEmail(
                            candidateData.email,
                            candidateName,
                            jobData.title,
                            jobData.company_name,
                            targetStageName,
                            jobLink,
                            jobData.id
                        );
                    }
                } catch (emailError) {
                    // Don't fail the stage update if email fails
                }
            }
        } catch (err) {
            alert("Failed to update stage. Please try again.");
        }
    };

    const handleDragStart = (e, application) => {
        setDraggedApplication(application);
        // Some browsers require data to be set for drag to start
        try {
            e.dataTransfer.setData("text/plain", application.id);
        } catch (_) {}
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = async (e, targetStageName) => {
        e.preventDefault();
        e.stopPropagation();
        if (!draggedApplication || !targetStageName) return;
        setPendingStageChange({
            type: "drag",
            application: draggedApplication,
            targetStageName,
        });
        setShowStageChangeWarning(true);
        setDraggedApplication(null);
    };

    const handleDragEnd = () => {
        setDraggedApplication(null);
    };

    // Touch handlers for mobile/tablet drag and drop
    const handleTouchStart = useCallback((e, application) => {
        const touch = e.touches[0];
        setTouchStartPos({ x: touch.clientX, y: touch.clientY });
        setTouchDragApp(application);
        setTouchPosition({ x: touch.clientX, y: touch.clientY });
    }, []);

    const handleTouchMove = useCallback(
        (e) => {
            if (!touchDragApp) return;

            const touch = e.touches[0];
            const deltaX = Math.abs(touch.clientX - touchStartPos.x);
            const deltaY = Math.abs(touch.clientY - touchStartPos.y);

            // Start dragging if moved past threshold
            if (
                !isDraggingTouch &&
                (deltaX > touchThreshold || deltaY > touchThreshold)
            ) {
                setIsDraggingTouch(true);
                // Prevent scrolling when dragging
                e.preventDefault();
            }

            if (isDraggingTouch) {
                e.preventDefault();
                setTouchPosition({ x: touch.clientX, y: touch.clientY });
            }
        },
        [touchDragApp, touchStartPos, isDraggingTouch]
    );

    const handleTouchEnd = useCallback(
        async (e) => {
            if (!touchDragApp || !isDraggingTouch) {
                setTouchDragApp(null);
                setIsDraggingTouch(false);
                return;
            }

            // Find which stage the card was dropped on
            let targetStageName = null;
            const dropX = touchPosition.x;
            const dropY = touchPosition.y;

            for (const [stageName, ref] of Object.entries(stageRefs.current)) {
                if (ref) {
                    const rect = ref.getBoundingClientRect();
                    if (
                        dropX >= rect.left &&
                        dropX <= rect.right &&
                        dropY >= rect.top &&
                        dropY <= rect.bottom
                    ) {
                        targetStageName = stageName;
                        break;
                    }
                }
            }

            if (
                targetStageName &&
                targetStageName !== (touchDragApp.stage || "Applications")
            ) {
                setPendingStageChange({
                    type: "touch",
                    application: touchDragApp,
                    targetStageName,
                });
                setShowStageChangeWarning(true);
            }

            setTouchDragApp(null);
            setIsDraggingTouch(false);
        },
        [touchDragApp, isDraggingTouch, touchPosition]
    );

    // Add/remove global touch listeners when dragging
    useEffect(() => {
        if (touchDragApp) {
            const handleGlobalTouchMove = (e) => {
                if (isDraggingTouch) {
                    e.preventDefault();
                }
            };
            document.addEventListener("touchmove", handleGlobalTouchMove, {
                passive: false,
            });
            return () => {
                document.removeEventListener(
                    "touchmove",
                    handleGlobalTouchMove
                );
            };
        }
    }, [touchDragApp, isDraggingTouch]);

    // Status options for the dropdown
    const statusOptions = [
        { label: "Applied", value: "applied", display: "Applied" },
        {
            label: "Good fit",
            value: "screening",
            display: "Good fit",
            customStatus: "good_fit",
        },
        {
            label: "Interview scheduled",
            value: "interview",
            display: "Interview scheduled",
        },
        {
            label: "Maybe",
            value: "screening",
            display: "Maybe",
            customStatus: "maybe",
        },
        { label: "Disqualified", value: "rejected", display: "Disqualified" },
        { label: "Offer sent", value: "offer", display: "Offer sent" },
    ];

    // Handle status change from dropdown (UI only, no database update)
    const handleStatusChange = (
        applicationId,
        newStatus,
        newDisplay,
        customStatus
    ) => {
        // Only update local state for UI display - no database changes
        setApplications((prev) =>
            prev.map((a) =>
                a.id === applicationId
                    ? {
                          ...a,
                          displayStatus: newDisplay, // Store display text
                          customStatus: customStatus || null, // Store custom status for display
                      }
                    : a
            )
        );

        setOpenStatusMenu(null); // Close the menu
    };

    const closeStageChangeWarning = () => {
        setShowStageChangeWarning(false);
        setPendingStageChange(null);
    };

    const finalizeStageChangeWithNotificationChoice = async (
        notifyApplicant
    ) => {
        const pending = pendingStageChange;
        setShowStageChangeWarning(false);
        setPendingStageChange(null);
        if (!pending) return;

        if (pending.type === "dropdownStatus") {
            handleStatusChange(
                pending.applicationId,
                pending.newStatus,
                pending.newDisplay,
                pending.customStatus
            );
            return;
        }

        await applyStageChange(
            pending.application,
            pending.targetStageName,
            notifyApplicant
        );
    };

    // Get display text for status
    const getStatusDisplay = (app) => {
        // If displayStatus is set (from UI change), use it
        if (app.displayStatus) return app.displayStatus;

        // If we have a custom status, use it to determine display
        if (app.customStatus === "good_fit") return "Good fit";
        if (app.customStatus === "maybe") return "Maybe";

        // Otherwise, find the matching option based on actual status
        const status = app.status || "applied";
        if (status === "screening") {
            // Default to "Good fit" if no custom status
            return "Good fit";
        }
        const option = statusOptions.find((opt) => opt.value === status);
        return option ? option.display : status.replace("_", " ");
    };

    // Handle favorite toggle
    const handleToggleFavorite = async (e, applicationId) => {
        e.stopPropagation();
        e.preventDefault();

        const isFavorited = favoritedApplications.has(applicationId);

        try {
            const { error } = await supabase
                .from("applications")
                .update({ is_favorite: !isFavorited })
                .eq("id", applicationId);

            if (error) throw error;

            // Update local state
            setFavoritedApplications((prev) => {
                const newSet = new Set(prev);
                if (isFavorited) {
                    newSet.delete(applicationId);
                } else {
                    newSet.add(applicationId);
                }
                return newSet;
            });

            // Update applications state
            setApplications((prev) =>
                prev.map((a) =>
                    a.id === applicationId
                        ? { ...a, is_favorite: !isFavorited }
                        : a
                )
            );
        } catch (err) {
            // Failed to toggle favorite
            alert("Failed to update favorite. Please try again.");
        }
    };

    // Helper functions for step 3 requirements
    const addSkill = (skill) => {
        if (skill.trim() && !newJobForm.requiredSkills.includes(skill.trim())) {
            setNewJobForm((p) => ({
                ...p,
                requiredSkills: [...p.requiredSkills, skill.trim()],
            }));
            setSkillInput("");
        }
    };

    const removeSkill = (skillIndex) => {
        setNewJobForm((p) => ({
            ...p,
            requiredSkills: p.requiredSkills.filter((_, i) => i !== skillIndex),
        }));
    };

    const addScreeningOption = () => {
        setCurrentOptions([...currentOptions, ""]);
    };

    const updateScreeningOption = (index, value) => {
        const updated = [...currentOptions];
        updated[index] = value;
        setCurrentOptions(updated);
    };

    const removeScreeningOption = (index) => {
        setCurrentOptions(currentOptions.filter((_, i) => i !== index));
    };

    const addScreeningQuestion = () => {
        if (currentQuestion.trim()) {
            if (currentQuestionType === "text") {
                const question = {
                    question: currentQuestion.trim(),
                    type: "text",
                };
                setScreeningQuestionsList([
                    ...screeningQuestionsList,
                    question,
                ]);
                setCurrentQuestion("");
                setCurrentQuestionType("multiple-choice");
                setCurrentOptions([""]);
            } else {
                if (currentOptions.some((opt) => opt.trim())) {
                    const question = {
                        question: currentQuestion.trim(),
                        type: "multiple-choice",
                        options: currentOptions.filter((opt) => opt.trim()),
                    };
                    setScreeningQuestionsList([
                        ...screeningQuestionsList,
                        question,
                    ]);
                    setCurrentQuestion("");
                    setCurrentQuestionType("multiple-choice");
                    setCurrentOptions([""]);
                }
            }
        }
    };

    const removeScreeningQuestion = (index) => {
        setScreeningQuestionsList(
            screeningQuestionsList.filter((_, i) => i !== index)
        );
    };

    useEffect(() => {
        if (showPromotionModal) {
            setSelectedPromotionPlan("free");
        }
    }, [showPromotionModal]);

    const handlePromotionAction = (promote) => {
        setShowPromotionModal(false);
        if (promote) {
            setShowPurchaseModal(true);
        }
    };

    const handleClosePurchaseModal = () => {
        setShowPurchaseModal(false);
    };

    const handleNextStep = () => {
        if (jobFormStep === 2) {
            // Validate step 2 required fields
            if (
                !newJobForm.jobTitle ||
                !newJobForm.employmentType ||
                !newJobForm.workPreference
            ) {
                alert(
                    "Please fill in all required fields (Job Title, Employment Type, Work preference)"
                );
                return;
            }
            setJobFormStep(3);
        }
    };

    const handleBackStep = () => {
        if (jobFormStep === 3) {
            setJobFormStep(2);
        }
    };

    const createJobListing = async () => {
        if (!user) {
            alert("You must be logged in as an employer to create a job.");
            return;
        }
        if (!newJobForm.jobTitle) {
            alert("Please fill at least Job Title.");
            return;
        }
        setSavingNewJob(true);
        try {
            await ensureListingCreditAvailable(user.id, user.email);

            const employerProfile = user?.user_metadata?.employer_profile || {};
            const companyInfo = employerProfile.companyInfo || {};

            // Build location string from country/province
            const locationParts = [];
            if (newJobForm.province && newJobForm.province !== "__NA__")
                locationParts.push(newJobForm.province);
            if (newJobForm.country) locationParts.push(newJobForm.country);
            const location = locationParts.join(", ") || "";

            // Include any unsaved screening question that's currently being typed
            let finalScreeningQuestionsList = [...screeningQuestionsList];
            if (currentQuestion.trim()) {
                if (currentQuestionType === "text") {
                    const pendingQuestion = {
                        question: currentQuestion.trim(),
                        type: "text",
                    };
                    finalScreeningQuestionsList.push(pendingQuestion);
                } else if (currentOptions.some((opt) => opt.trim())) {
                    const pendingQuestion = {
                        question: currentQuestion.trim(),
                        type: "multiple-choice",
                        options: currentOptions.filter((opt) => opt.trim()),
                    };
                    finalScreeningQuestionsList.push(pendingQuestion);
                }
            }

            const { data, error } = await supabase
                .from("internal_job_listings")
                .insert({
                    employer_id: user.id,
                    title: newJobForm.jobTitle,
                    company_name:
                        companyInfo.companyName ||
                        companyInfo.companyname ||
                        "",
                    location: location,
                    employment_type: newJobForm.employmentType,
                    work_preference: newJobForm.workPreference,
                    specialty: newJobForm.specialty || null,
                    qualifications: newJobForm.qualifications,
                    experience_requirements: newJobForm.experienceRequirements,
                    requires_license:
                        newJobForm.locationOrLicenceRestrictions === "yes",
                    sponsorship_available: newJobForm.visaSponsorshipOffered,
                    salary_range: newJobForm.salaryRange,
                    benefits: newJobForm.benefits,
                    job_description:
                        newJobForm.about || newJobForm.roleDescription,
                    contact_first_name: companyInfo.firstName || "",
                    contact_last_name: companyInfo.lastName || "",
                    contact_email: companyInfo.workEmail || "",
                    company_url: companyInfo.companyUrl || "",
                    industry: companyInfo.industry || "",
                    company_description: companyInfo.about || "",
                    logo_filename: companyInfo.logoFileName || null,
                    social_media_links: companyInfo.socialMediaLinks || null,
                    role_description: newJobForm.roleDescription,
                    preferred_candidate: newJobForm.preferredCandidate,
                    duties_and_responsibilities:
                        newJobForm.dutiesAndResponsibilities,
                    requirements_specialty:
                        newJobForm.requirementsSpecialty ||
                        newJobForm.specialty ||
                        null,
                    experience_level: newJobForm.experienceLevel || null,
                    years_of_experience: newJobForm.yearsOfExperience || null,
                    budget_for_role: newJobForm.budgetForRole || null,
                    salary_currency: newJobForm.salaryCurrency || "ZAR",
                    salary_period: newJobForm.salaryPeriod || "yearly",
                    salary_range: newJobForm.budgetForRole || null, // Also save to salary_range for display compatibility
                    required_skills: Array.isArray(newJobForm.requiredSkills)
                        ? newJobForm.requiredSkills
                        : [],
                    other_preferences: newJobForm.otherPreferences,
                    country: newJobForm.country || null,
                    country_code: newJobForm.countryCode || null,
                    province:
                        newJobForm.province && newJobForm.province !== "__NA__"
                            ? newJobForm.province
                            : null,
                    location_or_licence_restrictions:
                        newJobForm.locationOrLicenceRestrictions,
                    location_or_licence_restrictions_text:
                        newJobForm.locationOrLicenceRestrictionsText || "",
                    screening_questions: newJobForm.screeningQuestions,
                    screening_questions_list:
                        finalScreeningQuestionsList.length > 0
                            ? finalScreeningQuestionsList
                            : null,
                    drivers_license_required: newJobForm.driversLicenseRequired,
                    drivers_license_answer: driversLicenseAnswer,
                    visa_sponsorship_offered: newJobForm.visaSponsorshipOffered,
                    visa_sponsorship_answer: visaSponsorshipAnswer,
                    status: "published",
                    is_dev_job: newJobForm.is_dev_job || false,
                })
                .select("id")
                .single();

            if (error) throw error;

            // Refresh job list and select the newly created job
            setShowNewJobLeaveWarning(false);
            setShowNewJobModal(false);
            setJobFormStep(2);
            // Reset form
            setNewJobForm(INITIAL_NEW_JOB_FORM);
            setSkillInput("");
            setCurrentQuestion("");
            setCurrentOptions([""]);
            setScreeningQuestionsList([]);
            setDriversLicenseAnswer(null);
            setVisaSponsorshipAnswer(null);
            // reload jobs
            const { data: jobs } = await supabase
                .from("internal_job_listings")
                .select("id,title,specialty")
                .eq("employer_id", user.id)
                .neq("status", "closed") // Exclude archived jobs
                .order("created_at", { ascending: false });
            setJobListings(jobs || []);
            if (data?.id) {
                setSelectedJobId(data.id);
                navigate(`/employer/hiring-pipeline?jobId=${data.id}`);
            }
            // Refresh credits after job creation so count updates immediately
            await refreshCredits();
            window.dispatchEvent(new Event("refreshCredits"));
            // Temporarily commented out - promotion modal after job creation
            // setShowPromotionModal(true);
        } catch (err) {
            if (err?.code === NO_LISTING_CREDITS_ERROR) {
                // Show error message to user
                alert(
                    err.message ||
                        "You have reached your job listing limit. Please upgrade to post more jobs."
                );
                // Show upgrade modal for all users who run out of listings (free or paid)
                setShowUpgradeModal(true);
                return;
            }
            // Failed to create job listing
            alert("Failed to create job listing. Please try again.");
        } finally {
            setSavingNewJob(false);
        }
    };

    return (
        <>
            <style>{`
            @media (max-width: 1024px) {
                .enhanced-hiring-page {
                    padding-bottom: 4rem !important;
                    background: #f9fafb !important;
                }
                .enhanced-hiring-header {
                    padding: 1rem !important;
                }
                .enhanced-hiring-header-inner {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 1rem !important;
                }
                .enhanced-hiring-header-inner select {
                    width: 100% !important;
                    -webkit-appearance: none !important;
                    -moz-appearance: none !important;
                    appearance: none !important;
                    background-image: none !important;
                }
                .enhanced-hiring-header-inner select::-ms-expand {
                    display: none !important;
                }
                .enhanced-hiring-header-inner .inline-block,
                .enhanced-hiring-header-inner .flex.items-center {
                    width: 100% !important;
                }
                .enhanced-hiring-actions {
                    width: 100% !important;
                    justify-content: stretch !important;
                }
                .enhanced-hiring-actions button {
                    width: 100% !important;
                    justify-content: center !important;
                }
                .enhanced-hiring-pipeline {
                    padding: 1rem !important;
                }
                .enhanced-hiring-stage-grid {
                    display: flex !important;
                    gap: 0.75rem !important;
                    overflow-x: auto !important;
                    padding-bottom: 0.5rem !important;
                }
                .enhanced-hiring-stage-grid::-webkit-scrollbar {
                    display: none !important;
                }
                .enhanced-hiring-stage-column {
                    flex: 0 0 240px !important;
                    min-width: 240px !important;
                }
                .enhanced-hiring-stage-header h3 {
                    font-size: 0.95rem !important;
                }
                .enhanced-hiring-stage-card {
                    padding: 0.75rem !important;
                }
                .enhanced-hiring-favorite-btn {
                    top: 0.5rem !important;
                    right: 0.5rem !important;
                }
                .enhanced-hiring-card-top {
                    gap: 0.75rem !important;
                }
                .enhanced-hiring-status-row {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.35rem !important;
                }
                .enhanced-hiring-status-button {
                    font-size: 0.85rem !important;
                }
                .enhanced-hiring-date {
                    font-size: 0.75rem !important;
                }
                .enhanced-hiring-modal-content {
                    top: 0 !important;
                    width: 95% !important;
                    margin: 1rem auto !important;
                    border-radius: 1rem !important;
                }
                .enhanced-hiring-modal-header {
                    padding: 1rem !important;
                }
                .enhanced-hiring-modal-body {
                    padding: 1rem !important;
                }
                .enhanced-hiring-modal-body .grid {
                    gap: 0.75rem !important;
                }
                .enhanced-hiring-chip-row {
                    flex-wrap: wrap !important;
                    gap: 0.5rem !important;
                }
                .enhanced-hiring-modal-footer {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    padding: 1rem !important;
                    gap: 0.75rem !important;
                }
                .enhanced-hiring-modal-footer button {
                    width: 100% !important;
                }
                
                /* Touch drag styles for mobile/tablet */
                .touch-drag-preview {
                    position: fixed;
                    pointer-events: none;
                    z-index: 9999;
                    transform: translate(-50%, -50%);
                    opacity: 0.9;
                    width: 220px;
                    background: white;
                    border: 2px solid #3b82f6;
                    border-radius: 8px;
                    padding: 12px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
                }
                
                .touch-drag-active {
                    opacity: 0.4 !important;
                }
                
                .touch-drop-highlight {
                    background: rgba(59, 130, 246, 0.1) !important;
                    border: 2px dashed #3b82f6 !important;
                    border-radius: 8px;
                }
            }
        `}</style>
            {showStageChangeWarning && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                    <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-200 overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">
                                WARNING:
                            </h2>
                            <button
                                type="button"
                                onClick={closeStageChangeWarning}
                                className="text-gray-400 hover:text-gray-600"
                                aria-label="Close"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="text-sm text-gray-700">
                                This will update the applicant&apos;s stage in
                                the pipeline. Choose whether to notify them by
                                email about this change, or update without
                                sending an email.
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 p-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() =>
                                    finalizeStageChangeWithNotificationChoice(
                                        false
                                    )
                                }
                                className="px-4 py-2 rounded-full border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 transition-colors text-sm font-medium"
                            >
                                Update without mailing
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    finalizeStageChangeWithNotificationChoice(
                                        true
                                    )
                                }
                                className="px-4 py-2 rounded-full bg-black text-white hover:bg-gray-800 transition-colors text-sm font-medium"
                            >
                                Update &amp; Notify
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="min-h-screen bg-white enhanced-hiring-page">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 enhanced-hiring-header">
                    <div className="flex justify-between items-start enhanced-hiring-header-inner">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-4">
                                Hiring Pipeline
                            </h1>
                            {jobListings.length > 0 && (
                                <>
                                    {(selectedJobId === ALL_JOBS_VALUE
                                        ? allJobSpecialties.length > 0
                                        : !!selectedJob) && (
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {selectedJobId ===
                                            ALL_JOBS_VALUE ? (
                                                allJobSpecialties.map((s) => (
                                                    <span
                                                        key={s}
                                                        className="inline-flex bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium"
                                                    >
                                                        {s}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="inline-flex bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                                    {selectedJob?.specialty ||
                                                        "Clinical Medicine"}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <div className="relative flex sm:flex-row flex-col items-center gap-2">
                                            <label
                                                htmlFor="job-select"
                                                className="text-lg"
                                            >
                                                Showing applications for:
                                            </label>
                                            <select
                                                id="job-select"
                                                value={selectedJobId}
                                                onChange={(e) =>
                                                    handleSelectJob(
                                                        e.target.value
                                                    )
                                                }
                                                className="appearance-none bg-transparent border border-gray-300 p-2 rounded-full text-lg font-bold text-gray-900 cursor-pointer pr-8 focus:outline-none hover:text-gray-700"
                                                style={{
                                                    WebkitAppearance: "none",
                                                    MozAppearance: "none",
                                                    appearance: "none",
                                                    backgroundImage: "none",
                                                }}
                                            >
                                                <option value={ALL_JOBS_VALUE}>
                                                    All Jobs
                                                </option>
                                                {jobListings.map((j) => (
                                                    <option
                                                        key={j.id}
                                                        value={j.id}
                                                    >
                                                        {j.title || j.id}
                                                    </option>
                                                ))}
                                            </select>
                                            <svg
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-900 pointer-events-none"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-3 enhanced-hiring-actions">
                            <button
                                onClick={async () => {
                                    // Check if user has no remaining listings (free or paid)
                                    if (credits?.remainingListings <= 0) {
                                        const message = credits?.isFreePlan
                                            ? "Your Free Starter job post is already live. Upgrade or purchase a plan to post more roles."
                                            : "You have used all of your purchased job listings. Please buy additional listings to post more jobs.";
                                        alert(message);
                                        setShowUpgradeModal(true);
                                        return;
                                    }
                                    setShowNewJobModal(true);
                                    setJobFormStep(2);
                                }}
                                className="inline-flex items-center px-4 py-2 bg-black text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors"
                            >
                                <svg
                                    className="w-4 h-4 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                New Job Post
                            </button>
                        </div>
                    </div>
                </div>

                {/* Pipeline - mobile view (accordion) */}
                <div className="px-4 pb-6 pt-4 md:hidden enhanced-hiring-pipeline">
                    <div className="space-y-3">
                        {stages.map((stage) => {
                            const apps = getApplicationsForStage(stage.name);
                            const stageCount = apps.length;
                            const isOpen = openMobileStage === stage.name;

                            return (
                                <div
                                    key={stage.name}
                                    className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setOpenMobileStage(
                                                isOpen ? null : stage.name
                                            )
                                        }
                                        className="w-full flex items-center justify-between px-4 py-3"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-gray-900">
                                                {stage.name}
                                            </span>
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-700 text-xs font-medium">
                                                {stageCount}
                                            </span>
                                        </div>
                                        <svg
                                            className={`w-5 h-5 text-gray-700 transition-transform ${
                                                isOpen ? "rotate-180" : ""
                                            }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </button>

                                    {isOpen && (
                                        <div className="px-4 pb-4">
                                            <div className="h-1 bg-black/90 rounded mb-3" />
                                            <div className="space-y-2">
                                                {apps.length === 0 ? (
                                                    <div className="text-sm text-gray-500">
                                                        No candidates.
                                                    </div>
                                                ) : (
                                                    apps.map((app) => (
                                                        <div
                                                            key={app.id}
                                                            className="relative bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow enhanced-hiring-stage-card"
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={(e) =>
                                                                    handleToggleFavorite(
                                                                        e,
                                                                        app.id
                                                                    )
                                                                }
                                                                className="absolute top-3 right-3 z-10 flex-shrink-0 p-1 hover:opacity-70 transition-opacity enhanced-hiring-favorite-btn"
                                                                aria-label={
                                                                    favoritedApplications.has(
                                                                        app.id
                                                                    )
                                                                        ? "Remove from favorites"
                                                                        : "Add to favorites"
                                                                }
                                                            >
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    fill={
                                                                        favoritedApplications.has(
                                                                            app.id
                                                                        )
                                                                            ? "currentColor"
                                                                            : "none"
                                                                    }
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                    style={{
                                                                        color: favoritedApplications.has(
                                                                            app.id
                                                                        )
                                                                            ? "#000000"
                                                                            : "#000000",
                                                                    }}
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth={
                                                                            2
                                                                        }
                                                                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                                                    />
                                                                </svg>
                                                            </button>
                                                            <div className="flex items-start enhanced-hiring-card-top pr-7">
                                                                <div className="w-5 h-5 bg-gray-200 rounded-full flex-shrink-0 mr-3 overflow-hidden">
                                                                    {app
                                                                        .users_profiles
                                                                        ?.profile_image_url ? (
                                                                        <img
                                                                            src={
                                                                                app
                                                                                    .users_profiles
                                                                                    .profile_image_url
                                                                            }
                                                                            alt="avatar"
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    ) : null}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <h6
                                                                        onClick={() =>
                                                                            app.user_id &&
                                                                            navigate(
                                                                                `/employer/candidate/${app.user_id}?jobId=${selectedJobId}`,
                                                                                {
                                                                                    replace: true,
                                                                                }
                                                                            )
                                                                        }
                                                                        className="font-medium text-gray-900 truncate cursor-pointer hover:text-gray-700 hover:underline transition-colors"
                                                                    >
                                                                        {(() => {
                                                                            const p =
                                                                                app.users_profiles;
                                                                            if (
                                                                                p &&
                                                                                (p.firstname ||
                                                                                    p.lastname)
                                                                            ) {
                                                                                return `${
                                                                                    p.firstname ||
                                                                                    ""
                                                                                } ${
                                                                                    p.lastname ||
                                                                                    ""
                                                                                }`.trim();
                                                                            }
                                                                            if (
                                                                                p &&
                                                                                p.email
                                                                            ) {
                                                                                return p.email.split(
                                                                                    "@"
                                                                                )[0];
                                                                            }
                                                                            return "Candidate";
                                                                        })()}
                                                                    </h6>
                                                                </div>
                                                            </div>
                                                            <div className="mt-2 bg-[#cfeee6] rounded-md px-3 py-1 flex items-center justify-between relative enhanced-hiring-status-row">
                                                                <div className="relative flex-1">
                                                                    <button
                                                                        onClick={(
                                                                            e
                                                                        ) => {
                                                                            e.stopPropagation();
                                                                            setOpenStatusMenu(
                                                                                openStatusMenu ===
                                                                                    app.id
                                                                                    ? null
                                                                                    : app.id
                                                                            );
                                                                        }}
                                                                        className="text-sm font-medium text-gray-800 hover:text-gray-900 cursor-pointer flex items-center gap-1 enhanced-hiring-status-button"
                                                                    >
                                                                        {getStatusDisplay(
                                                                            app
                                                                        )}
                                                                        <svg
                                                                            className="w-4 h-4"
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                            viewBox="0 0 24 24"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                d="M19 9l-7 7-7-7"
                                                                            />
                                                                        </svg>
                                                                    </button>

                                                                    {/* Status Dropdown Menu */}
                                                                    {openStatusMenu ===
                                                                        app.id && (
                                                                        <>
                                                                            <div
                                                                                className="fixed inset-0 z-10"
                                                                                onClick={() =>
                                                                                    setOpenStatusMenu(
                                                                                        null
                                                                                    )
                                                                                }
                                                                            ></div>
                                                                            <div className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                                                                                {statusOptions.map(
                                                                                    (
                                                                                        option
                                                                                    ) => (
                                                                                        <button
                                                                                            key={
                                                                                                option.label
                                                                                            }
                                                                                            onClick={(
                                                                                                e
                                                                                            ) => {
                                                                                                e.stopPropagation();
                                                                                                setOpenStatusMenu(
                                                                                                    null
                                                                                                );
                                                                                                setPendingStageChange(
                                                                                                    {
                                                                                                        type: "dropdownStatus",
                                                                                                        applicationId:
                                                                                                            app.id,
                                                                                                        newStatus:
                                                                                                            option.value,
                                                                                                        newDisplay:
                                                                                                            option.display,
                                                                                                        customStatus:
                                                                                                            option.customStatus,
                                                                                                    }
                                                                                                );
                                                                                                setShowStageChangeWarning(
                                                                                                    true
                                                                                                );
                                                                                            }}
                                                                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                                                                                app.displayStatus ===
                                                                                                    option.display ||
                                                                                                (!app.displayStatus &&
                                                                                                    app.status ===
                                                                                                        option.value &&
                                                                                                    app.customStatus ===
                                                                                                        option.customStatus)
                                                                                                    ? "bg-gray-50 font-medium text-gray-900"
                                                                                                    : "text-gray-700"
                                                                                            }`}
                                                                                        >
                                                                                            {
                                                                                                option.display
                                                                                            }
                                                                                        </button>
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <span className="text-sm font-medium text-gray-700 ml-2 enhanced-hiring-date">
                                                                    {new Date(
                                                                        app.application_date ||
                                                                            app.created_at ||
                                                                            Date.now()
                                                                    ).toLocaleDateString(
                                                                        "en-US",
                                                                        {
                                                                            month: "short",
                                                                            day: "numeric",
                                                                        }
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Pipeline - desktop view */}
                <div className="hidden md:block px-6 pb-6 pt-4 enhanced-hiring-pipeline">
                    <div className="overflow-x-auto pb-4">
                        <div className="grid grid-cols-6 gap-6 min-w-[280px] enhanced-hiring-stage-grid">
                            {stages.map((stage, stageIndex) => {
                                const stageCount = getApplicationsForStage(
                                    stage.name
                                ).length;
                                return (
                                    <div
                                        key={stage.name}
                                        className="w-full enhanced-hiring-stage-column"
                                        ref={(el) => {
                                            stageRefs.current[stage.name] = el;
                                        }}
                                    >
                                        <div className="flex items-center gap-2 mb-2 enhanced-hiring-stage-header">
                                            <h3 className="font-bold text-gray-900">
                                                {stage.name}
                                            </h3>
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-700 text-xs font-medium">
                                                {stageCount}
                                            </span>
                                        </div>
                                        <div className="h-1 bg-black/90 rounded mb-3" />
                                        <div
                                            className="space-y-2 min-h-[80px]"
                                            onDragEnter={handleDragOver}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) =>
                                                handleDrop(e, stage.name)
                                            }
                                        >
                                            {(() => {
                                                const apps =
                                                    getApplicationsForStage(
                                                        stage.name
                                                    );
                                                if (apps.length === 0) {
                                                    return (
                                                        <div className="text-sm text-gray-500">
                                                            No candidates.
                                                        </div>
                                                    );
                                                }
                                                return apps.map((app) => (
                                                    <div
                                                        key={app.id}
                                                        draggable
                                                        onDragStart={(e) =>
                                                            handleDragStart(
                                                                e,
                                                                app
                                                            )
                                                        }
                                                        onDragEnd={
                                                            handleDragEnd
                                                        }
                                                        onTouchStart={(e) =>
                                                            handleTouchStart(
                                                                e,
                                                                app
                                                            )
                                                        }
                                                        onTouchMove={
                                                            handleTouchMove
                                                        }
                                                        onTouchEnd={
                                                            handleTouchEnd
                                                        }
                                                        className={`relative bg-white border border-gray-200 rounded-lg py-4 px-2 min-h-[96px] hover:shadow-md transition-shadow cursor-move enhanced-hiring-stage-card ${
                                                            isDraggingTouch &&
                                                            touchDragApp?.id ===
                                                                app.id
                                                                ? "touch-drag-active"
                                                                : ""
                                                        }`}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={(e) =>
                                                                handleToggleFavorite(
                                                                    e,
                                                                    app.id
                                                                )
                                                            }
                                                            className="absolute top-1 right-3 z-10 flex-shrink-0 p-1 hover:opacity-70 transition-opacity enhanced-hiring-favorite-btn"
                                                            aria-label={
                                                                favoritedApplications.has(
                                                                    app.id
                                                                )
                                                                    ? "Remove from favorites"
                                                                    : "Add to favorites"
                                                            }
                                                        >
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill={
                                                                    favoritedApplications.has(
                                                                        app.id
                                                                    )
                                                                        ? "#fcf003"
                                                                        : "none"
                                                                }
                                                                stroke={
                                                                    favoritedApplications.has(
                                                                        app.id
                                                                    )
                                                                        ? "#fcf003"
                                                                        : "currentColor"
                                                                }
                                                                viewBox="0 0 24 24"
                                                                style={{
                                                                    color: favoritedApplications.has(
                                                                        app.id
                                                                    )
                                                                        ? "#000000"
                                                                        : "#000000",
                                                                }}
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                                                />
                                                            </svg>
                                                        </button>
                                                        <div className="flex items-center enhanced-hiring-card-top pr-7">
                                                            <div className="w-5 h-5 bg-gray-200 rounded-full flex-shrink-0 mr-2 overflow-hidden">
                                                                {app
                                                                    .users_profiles
                                                                    ?.profile_image_url ? (
                                                                    <img
                                                                        src={
                                                                            app
                                                                                .users_profiles
                                                                                .profile_image_url
                                                                        }
                                                                        alt="avatar"
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                ) : null}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <h4
                                                                    onClick={() => {
                                                                        if (
                                                                            app.user_id
                                                                        ) {
                                                                            navigate(
                                                                                `/employer/candidate/${app.user_id}?jobId=${app.job_listing_id}`,
                                                                                {
                                                                                    replace: true,
                                                                                }
                                                                            );
                                                                        }
                                                                    }}
                                                                    className="font-medium text-gray-900 max-w-full break-words [overflow-wrap:anywhere] cursor-pointer hover:text-gray-700 hover:underline transition-colors"
                                                                >
                                                                    {(() => {
                                                                        const p =
                                                                            app.users_profiles;
                                                                        if (
                                                                            p &&
                                                                            (p.firstname ||
                                                                                p.lastname)
                                                                        ) {
                                                                            return `${
                                                                                p.firstname ||
                                                                                ""
                                                                            } ${
                                                                                p.lastname ||
                                                                                ""
                                                                            }`.trim();
                                                                        }
                                                                        if (
                                                                            p &&
                                                                            p.email
                                                                        ) {
                                                                            return p.email.split(
                                                                                "@"
                                                                            )[0];
                                                                        }
                                                                        return "Candidate";
                                                                    })()}
                                                                </h4>
                                                            </div>
                                                        </div>
                                                        <div className="mt-2 bg-gray-100 rounded-md px-3 py-1 flex items-center justify-between relative enhanced-hiring-status-row">
                                                            <span className="text-sm font-medium text-gray-800">
                                                                Applied:{" "}
                                                                {new Date(
                                                                    app.application_date ||
                                                                        app.created_at ||
                                                                        Date.now()
                                                                ).toLocaleDateString(
                                                                    "en-US",
                                                                    {
                                                                        month: "short",
                                                                        day: "numeric",
                                                                    }
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Touch Drag Preview for mobile/tablet */}
                {isDraggingTouch && touchDragApp && (
                    <div
                        className="touch-drag-preview"
                        ref={touchPreviewRef}
                        style={{
                            left: touchPosition.x,
                            top: touchPosition.y,
                        }}
                    >
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-gray-200 rounded-full flex-shrink-0 overflow-hidden">
                                {touchDragApp.users_profiles
                                    ?.profile_image_url && (
                                    <img
                                        src={
                                            touchDragApp.users_profiles
                                                .profile_image_url
                                        }
                                        alt="avatar"
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>
                            <span className="font-medium text-sm text-gray-900 truncate">
                                {(() => {
                                    const p = touchDragApp.users_profiles;
                                    if (p && (p.firstname || p.lastname)) {
                                        return `${p.firstname || ""} ${
                                            p.lastname || ""
                                        }`.trim();
                                    }
                                    if (p && p.email) {
                                        return p.email.split("@")[0];
                                    }
                                    return "Candidate";
                                })()}
                            </span>
                        </div>
                        <div className="mt-1 text-xs text-blue-600 font-medium">
                            Drop in a stage column
                        </div>
                    </div>
                )}

                {showPromotionModal && (
                    <>
                        <style>{`
                    @media (max-width: 1023px) {
                        .promotion-modal-free-post {
                            min-height: 200px !important;
                            display: flex !important;
                            flex-direction: column !important;
                            justify-content: center !important;
                        }
                    }
                    @media (max-width: 767px) {
                        .promotion-modal-free-post {
                            min-height: 180px !important;
                        }
                    }
                `}</style>
                        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
                            <div
                                className="absolute inset-0 bg-black/50"
                                onClick={() => setShowPromotionModal(false)}
                            />
                            <div className="relative w-full max-w-3xl bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden my-2 sm:my-auto max-h-[95vh] sm:max-h-none overflow-y-auto">
                                <div className="flex items-center justify-between px-3 py-2.5 sm:px-4 sm:py-3 md:px-6 md:py-4 border-b">
                                    <div>
                                        <p className="text-[10px] sm:text-xs md:text-sm text-gray-500">
                                            Step 3 of 3
                                        </p>
                                        <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">
                                            New job Posting
                                        </h2>
                                    </div>
                                    <button
                                        onClick={() =>
                                            setShowPromotionModal(false)
                                        }
                                        className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 flex-shrink-0"
                                        aria-label="Close"
                                    >
                                        <svg
                                            className="w-4 h-4 sm:w-5 sm:h-5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                                <div className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4 sm:pb-2 md:px-6 md:pt-6 md:pb-3 text-center">
                                    <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 px-1 sm:px-2 leading-tight">
                                        Promote your job post to get 3x more
                                        qualified applicants
                                    </h3>
                                </div>
                                <div className="px-3 pb-3 sm:px-4 sm:pb-4 md:px-6 md:pb-6">
                                    <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 flex flex-col lg:flex-row gap-3 sm:gap-4 md:gap-6">
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                setSelectedPromotionPlan("free")
                                            }
                                            onKeyDown={(e) =>
                                                e.key === "Enter" &&
                                                setSelectedPromotionPlan("free")
                                            }
                                            className={`promotion-modal-free-post flex-1 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 cursor-pointer transition-all ${
                                                selectedPromotionPlan === "free"
                                                    ? "border-2 border-gray-900 shadow-lg ring-2 ring-gray-900/10"
                                                    : "border border-gray-200 hover:border-gray-400 shadow-sm"
                                            }`}
                                        >
                                            <p className="text-xs sm:text-sm font-semibold text-gray-500 mb-1.5 sm:mb-2">
                                                Free post
                                            </p>
                                            <ul className="text-xs sm:text-sm text-gray-700 space-y-1 sm:space-y-2">
                                                <li className="flex items-center gap-1.5 sm:gap-2">
                                                    <span className="text-green-500 flex-shrink-0">
                                                        ✓
                                                    </span>
                                                    <span>
                                                        Shown in search results
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                setSelectedPromotionPlan(
                                                    "sponsored"
                                                )
                                            }
                                            onKeyDown={(e) =>
                                                e.key === "Enter" &&
                                                setSelectedPromotionPlan(
                                                    "sponsored"
                                                )
                                            }
                                            className={`flex-1 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 cursor-pointer transition-all ${
                                                selectedPromotionPlan ===
                                                "sponsored"
                                                    ? "border-2 border-gray-900 shadow-xl ring-2 ring-gray-900/10"
                                                    : "border border-gray-300 hover:border-gray-500 shadow-md"
                                            }`}
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                                <p className="text-xs sm:text-sm font-semibold text-gray-500">
                                                    Sponsored Jobs
                                                </p>
                                                <span className="px-2 py-0.5 text-[10px] sm:text-xs rounded-full bg-gray-900 text-white w-fit">
                                                    Recommended
                                                </span>
                                            </div>
                                            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-0.5 sm:mb-1">
                                                R749
                                            </div>
                                            <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 mb-2 sm:mb-3 md:mb-4">
                                                Per Month
                                            </p>
                                            <p className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-800 mb-1.5 sm:mb-2 md:mb-3">
                                                For companies hiring fast
                                            </p>
                                            <ul className="text-xs sm:text-sm text-gray-700 space-y-1 sm:space-y-1.5 md:space-y-2">
                                                {[
                                                    "Premium listing",
                                                    "Top-of-search placement",
                                                    "Recommended job matching",
                                                    "Job analytics",
                                                    "Team collaboration of 5-Seats",
                                                ].map((item) => (
                                                    <li
                                                        key={item}
                                                        className="flex items-start gap-1.5 sm:gap-2"
                                                    >
                                                        <span className="text-green-500 mt-0.5 flex-shrink-0">
                                                            ✓
                                                        </span>
                                                        <span className="leading-tight">
                                                            {item}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 px-3 pb-3 sm:px-4 sm:pb-4 md:px-6 md:pb-6">
                                    <button
                                        onClick={() =>
                                            handlePromotionAction(false)
                                        }
                                        className="w-full sm:w-1/2 px-3 sm:px-4 md:px-6 py-2.5 sm:py-2.5 md:py-3 rounded-full border border-gray-900 text-gray-900 text-xs sm:text-sm md:text-base font-medium hover:bg-gray-50 min-h-[44px] sm:min-h-0"
                                    >
                                        Post without promoting
                                    </button>
                                    <button
                                        onClick={() =>
                                            handlePromotionAction(true)
                                        }
                                        className="w-full sm:w-1/2 px-3 sm:px-4 md:px-6 py-2.5 sm:py-2.5 md:py-3 rounded-full bg-black text-white text-xs sm:text-sm md:text-base font-medium hover:bg-gray-900 min-h-[44px] sm:min-h-0"
                                    >
                                        Promote job
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {showUpgradeModal && (
                    <>
                        <style>{`
                    @media (max-width: 1023px) {
                        .upgrade-modal-content {
                            min-height: 200px !important;
                            display: flex !important;
                            flex-direction: column !important;
                            justify-content: center !important;
                        }
                    }
                    @media (max-width: 767px) {
                        .upgrade-modal-content {
                            min-height: 180px !important;
                        }
                    }
                `}</style>
                        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
                            <div
                                className="absolute inset-0 bg-black/50"
                                onClick={() => setShowUpgradeModal(false)}
                            />
                            <div className="relative w-full max-w-3xl bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden my-2 sm:my-auto max-h-[95vh] sm:max-h-none overflow-y-auto">
                                <div className="flex items-center justify-between px-3 py-2.5 sm:px-4 sm:py-3 md:px-6 md:py-4 border-b">
                                    <div>
                                        <p className="text-[10px] sm:text-xs md:text-sm text-gray-500">
                                            Step 1 of 3
                                        </p>
                                        <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">
                                            New job Posting
                                        </h2>
                                    </div>
                                    <button
                                        onClick={() =>
                                            setShowUpgradeModal(false)
                                        }
                                        className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 flex-shrink-0"
                                        aria-label="Close"
                                    >
                                        <svg
                                            className="w-4 h-4 sm:w-5 sm:h-5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                                <div className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4 sm:pb-2 md:px-6 md:pt-6 md:pb-3">
                                    <div className="mb-4 sm:mb-6">
                                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                                            Posting limit reached
                                        </h3>
                                        <p className="text-sm sm:text-base text-gray-700">
                                            To create a new listing, please
                                            sponsor or upgrade your plan to
                                            unlock additional job posts.
                                        </p>
                                    </div>
                                    <div className="upgrade-modal-content bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
                                        <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 border border-gray-300 shadow-xl">
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                                <p className="text-xs sm:text-sm font-semibold text-gray-900">
                                                    Sponsored Jobs
                                                </p>
                                                <span className="px-2 py-0.5 text-[10px] sm:text-xs rounded-full bg-gray-800 text-white w-fit">
                                                    Recommended
                                                </span>
                                            </div>
                                            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-0.5 sm:mb-1">
                                                R749
                                            </div>
                                            <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 mb-2 sm:mb-3 md:mb-4">
                                                Per Month
                                            </p>
                                            <p className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-800 mb-1.5 sm:mb-2 md:mb-3">
                                                For companies hiring fast
                                            </p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 sm:gap-y-1.5 md:gap-y-2">
                                                <ul className="text-xs sm:text-sm text-gray-700 space-y-1 sm:space-y-1.5 md:space-y-2">
                                                    {[
                                                        "Premium listing",
                                                        "Top-of-search placement",
                                                        "Recommended Job matching",
                                                    ].map((item) => (
                                                        <li
                                                            key={item}
                                                            className="flex items-start gap-1.5 sm:gap-2"
                                                        >
                                                            <span className="text-gray-900 mt-0.5 flex-shrink-0">
                                                                ✓
                                                            </span>
                                                            <span className="leading-tight">
                                                                {item}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <ul className="text-xs sm:text-sm text-gray-700 space-y-1 sm:space-y-1.5 md:space-y-2">
                                                    {[
                                                        "Job analytics",
                                                        "Team collaboration of 5-Seats",
                                                    ].map((item) => (
                                                        <li
                                                            key={item}
                                                            className="flex items-start gap-1.5 sm:gap-2"
                                                        >
                                                            <span className="text-gray-900 mt-0.5 flex-shrink-0">
                                                                ✓
                                                            </span>
                                                            <span className="leading-tight">
                                                                {item}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end px-3 pb-3 sm:px-4 sm:pb-4 md:px-6 md:pb-6">
                                    <button
                                        onClick={() => {
                                            setShowUpgradeModal(false);
                                            setShowPurchaseModal(true);
                                        }}
                                        className="px-4 sm:px-6 md:px-8 py-2.5 sm:py-2.5 md:py-3 rounded-full bg-black text-white text-xs sm:text-sm md:text-base font-medium hover:bg-gray-900 min-h-[44px] sm:min-h-0"
                                    >
                                        Promote job
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {showPurchaseModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div
                            className="absolute inset-0 bg-black/50"
                            onClick={handleClosePurchaseModal}
                        />
                        <div className="relative w-full max-w-2xl bg-white rounded-[32px] shadow-2xl overflow-hidden px-6 pt-6 pb-8">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <p className="text-sm text-gray-500">
                                        Promote job
                                    </p>
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        Complete payment to sponsor this listing
                                    </h2>
                                </div>
                                <button
                                    onClick={handleClosePurchaseModal}
                                    className="p-2 rounded-full hover:bg-gray-100"
                                    aria-label="Close payment modal"
                                >
                                    <svg
                                        className="w-5 h-5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 mb-6">
                                Purchase a Sponsored Job credit to unlock 3x
                                more visibility for your new post.
                            </p>
                            <JobListingPurchasePanel
                                onPurchaseRecorded={async (purchaseData) => {
                                    setShowPurchaseModal(false);
                                    // Wait a moment for database to commit, then refresh page to update all components
                                    setTimeout(() => {
                                        window.location.reload();
                                    }, 1500);
                                }}
                            />
                        </div>
                    </div>
                )}
                {showNewJobModal && (
                    <div className="fixed inset-0 z-50">
                        <div
                            className="absolute inset-0 bg-black/30"
                            onClick={() => {
                                setShowNewJobModal(false);
                                setJobFormStep(2);
                            }}
                        />
                        <div className="absolute inset-x-0 top-10 mx-auto w-[95%] max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden enhanced-hiring-modal-content">
                            <div className="flex items-center justify-between px-6 py-4 border-b enhanced-hiring-modal-header">
                                {/* modal heading */}
                                <h2 className="text-lg font-semibold">
                                    Create new job post
                                </h2>

                                {/* set job as dev mode checkbox */}
                                {user?.email.includes(
                                    "@fourminutemedicine.com"
                                ) && (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="devMode"
                                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                            checked={newJobForm.is_dev_job}
                                            onChange={(e) =>
                                                setNewJobForm((p) => ({
                                                    ...p,
                                                    is_dev_job:
                                                        e.target.checked,
                                                }))
                                            }
                                        />
                                        <label
                                            htmlFor="devMode"
                                            className="text-sm font-medium text-gray-700"
                                        >
                                            Set job as dev mode (for testing
                                            purposes)
                                        </label>
                                    </div>
                                )}

                                {/* close button */}
                                <button
                                    onClick={attemptCloseNewJobModal}
                                    className="p-2 rounded-full hover:bg-gray-100"
                                    aria-label="Close"
                                >
                                    <svg
                                        className="w-5 h-5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto enhanced-hiring-modal-body">
                                {/* Step 2: Job Description */}
                                {jobFormStep === 2 && (
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Job Title*
                                                </label>
                                                <input
                                                    className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                                    value={newJobForm.jobTitle}
                                                    onChange={(e) =>
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            jobTitle:
                                                                e.target.value,
                                                        }))
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Role category
                                                </label>
                                                <select
                                                    className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                                    value={
                                                        isCustomRoleCategory
                                                            ? "__OTHER__"
                                                            : newJobForm.specialty ||
                                                              ""
                                                    }
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        if (
                                                            value ===
                                                            "__OTHER__"
                                                        ) {
                                                            setIsCustomRoleCategory(
                                                                true
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    specialty:
                                                                        "",
                                                                })
                                                            );
                                                            return;
                                                        }
                                                        setIsCustomRoleCategory(
                                                            false
                                                        );
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            specialty: value,
                                                        }));
                                                    }}
                                                >
                                                    <option value="">
                                                        Select a role
                                                    </option>
                                                    {Array.isArray(
                                                        roleCategories
                                                    ) &&
                                                        roleCategories.map(
                                                            (s) => (
                                                                <option
                                                                    key={s}
                                                                    value={s}
                                                                >
                                                                    {s}
                                                                </option>
                                                            )
                                                        )}
                                                    <option value="__OTHER__">
                                                        Other
                                                    </option>
                                                </select>
                                                {isCustomRoleCategory && (
                                                    <input
                                                        className="mt-2 rounded-full border border-gray-300 px-4 py-2 w-full"
                                                        placeholder="Enter role category"
                                                        value={
                                                            newJobForm.specialty
                                                        }
                                                        onChange={(e) =>
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    specialty:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">
                                                Describe the role?
                                            </label>
                                            <textarea
                                                rows={3}
                                                className="w-full rounded-2xl border border-gray-200 px-4 py-3"
                                                value={
                                                    newJobForm.roleDescription
                                                }
                                                onChange={(e) =>
                                                    setNewJobForm((p) => ({
                                                        ...p,
                                                        roleDescription:
                                                            e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Role Requirements?
                                            </label>
                                            <textarea
                                                rows={3}
                                                className="w-full rounded-2xl border border-gray-200 px-4 py-3"
                                                placeholder="List the qualifications, skills and experience required for this position."
                                                value={
                                                    newJobForm.preferredCandidate
                                                }
                                                onChange={(e) => {
                                                    let value = e.target.value;
                                                    if (
                                                        value.length === 1 &&
                                                        !value.startsWith("●")
                                                    ) {
                                                        value = "●  " + value;
                                                    }
                                                    setNewJobForm((p) => ({
                                                        ...p,
                                                        preferredCandidate:
                                                            value,
                                                    }));
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        const textarea =
                                                            e.target;
                                                        const start =
                                                            textarea.selectionStart;
                                                        const end =
                                                            textarea.selectionEnd;
                                                        const value =
                                                            newJobForm.preferredCandidate ||
                                                            "";
                                                        const newValue =
                                                            value.substring(
                                                                0,
                                                                start
                                                            ) +
                                                            "\n●  " +
                                                            value.substring(
                                                                end
                                                            );
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            preferredCandidate:
                                                                newValue,
                                                        }));
                                                        setTimeout(() => {
                                                            textarea.selectionStart =
                                                                textarea.selectionEnd =
                                                                    start + 4;
                                                        }, 0);
                                                    }
                                                }}
                                                onFocus={(e) => {
                                                    if (
                                                        !newJobForm.preferredCandidate
                                                    ) {
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            preferredCandidate:
                                                                "●  ",
                                                        }));
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Duties and responsibilities?
                                            </label>
                                            <textarea
                                                rows={3}
                                                className="w-full rounded-2xl border border-gray-200 px-4 py-3"
                                                placeholder="List day-to-day responsibilities and expectations for this role."
                                                value={
                                                    newJobForm.dutiesAndResponsibilities
                                                }
                                                onChange={(e) => {
                                                    let value = e.target.value;
                                                    if (
                                                        value.length === 1 &&
                                                        !value.startsWith("●")
                                                    ) {
                                                        value = "●  " + value;
                                                    }
                                                    setNewJobForm((p) => ({
                                                        ...p,
                                                        dutiesAndResponsibilities:
                                                            value,
                                                    }));
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        const textarea =
                                                            e.target;
                                                        const start =
                                                            textarea.selectionStart;
                                                        const end =
                                                            textarea.selectionEnd;
                                                        const value =
                                                            newJobForm.dutiesAndResponsibilities ||
                                                            "";
                                                        const newValue =
                                                            value.substring(
                                                                0,
                                                                start
                                                            ) +
                                                            "\n●  " +
                                                            value.substring(
                                                                end
                                                            );
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            dutiesAndResponsibilities:
                                                                newValue,
                                                        }));
                                                        setTimeout(() => {
                                                            textarea.selectionStart =
                                                                textarea.selectionEnd =
                                                                    start + 4;
                                                        }, 0);
                                                    }
                                                }}
                                                onFocus={(e) => {
                                                    if (
                                                        !newJobForm.dutiesAndResponsibilities
                                                    ) {
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            dutiesAndResponsibilities:
                                                                "●  ",
                                                        }));
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Employment Type*
                                                </label>
                                                <select
                                                    className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                                    value={
                                                        isCustomEmploymentType
                                                            ? "__OTHER__"
                                                            : newJobForm.employmentType ||
                                                              ""
                                                    }
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        if (
                                                            value ===
                                                            "__OTHER__"
                                                        ) {
                                                            setIsCustomEmploymentType(
                                                                true
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    employmentType:
                                                                        "",
                                                                })
                                                            );
                                                            return;
                                                        }
                                                        setIsCustomEmploymentType(
                                                            false
                                                        );
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            employmentType:
                                                                value,
                                                        }));
                                                    }}
                                                >
                                                    <option value="">
                                                        Select employment type
                                                    </option>
                                                    <option value="full-time">
                                                        Full-time
                                                    </option>
                                                    <option value="part-time">
                                                        Part-time
                                                    </option>
                                                    <option value="contract">
                                                        Contract
                                                    </option>
                                                    <option value="temporary">
                                                        Temporary
                                                    </option>
                                                    <option value="__OTHER__">
                                                        Other
                                                    </option>
                                                </select>
                                                {isCustomEmploymentType && (
                                                    <input
                                                        className="mt-2 rounded-full border border-gray-300 px-4 py-2 w-full"
                                                        placeholder="Enter employment type"
                                                        value={
                                                            newJobForm.employmentType
                                                        }
                                                        onChange={(e) =>
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    employmentType:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Work preference*
                                                </label>
                                                <select
                                                    className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                                    value={
                                                        isCustomWorkPreference
                                                            ? "__OTHER__"
                                                            : newJobForm.workPreference ||
                                                              ""
                                                    }
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        if (
                                                            value ===
                                                            "__OTHER__"
                                                        ) {
                                                            setIsCustomWorkPreference(
                                                                true
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    workPreference:
                                                                        "",
                                                                })
                                                            );
                                                            return;
                                                        }
                                                        setIsCustomWorkPreference(
                                                            false
                                                        );
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            workPreference:
                                                                value,
                                                        }));
                                                    }}
                                                >
                                                    <option value="">
                                                        Select work preference
                                                    </option>
                                                    <option value="remote">
                                                        Remote
                                                    </option>
                                                    <option value="on-site">
                                                        On-site
                                                    </option>
                                                    <option value="hybrid">
                                                        Hybrid
                                                    </option>
                                                    <option value="__OTHER__">
                                                        Other
                                                    </option>
                                                </select>
                                                {isCustomWorkPreference && (
                                                    <input
                                                        className="mt-2 rounded-full border border-gray-300 px-4 py-2 w-full"
                                                        placeholder="Enter work preference"
                                                        value={
                                                            newJobForm.workPreference
                                                        }
                                                        onChange={(e) =>
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    workPreference:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Country*
                                                </label>
                                                <Select
                                                    options={[
                                                        ...[
                                                            ...countryList().getData(),
                                                        ].sort((a, b) =>
                                                            a.label ===
                                                            "South Africa"
                                                                ? -1
                                                                : b.label ===
                                                                  "South Africa"
                                                                ? 1
                                                                : a.label.localeCompare(
                                                                      b.label
                                                                  )
                                                        ),
                                                        {
                                                            value: "__OTHER__",
                                                            label: "Other",
                                                        },
                                                    ]}
                                                    value={
                                                        isCustomCountry
                                                            ? {
                                                                  value: "__OTHER__",
                                                                  label: "Other",
                                                              }
                                                            : countryList()
                                                                  .getData()
                                                                  .find(
                                                                      (c) =>
                                                                          c.value ===
                                                                              newJobForm.countryCode ||
                                                                          c.label ===
                                                                              newJobForm.country
                                                                  ) || null
                                                    }
                                                    onChange={(
                                                        selectedOption
                                                    ) => {
                                                        if (!selectedOption) {
                                                            setIsCustomCountry(
                                                                false
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    country: "",
                                                                    countryCode:
                                                                        "",
                                                                    province:
                                                                        "",
                                                                })
                                                            );
                                                            return;
                                                        }
                                                        if (
                                                            selectedOption.value ===
                                                            "__OTHER__"
                                                        ) {
                                                            setIsCustomCountry(
                                                                true
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    country: "",
                                                                    countryCode:
                                                                        "",
                                                                    province:
                                                                        "",
                                                                })
                                                            );
                                                            return;
                                                        }
                                                        setIsCustomCountry(
                                                            false
                                                        );
                                                        const countryCode =
                                                            selectedOption?.value ||
                                                            "";
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            country:
                                                                selectedOption?.label ||
                                                                "",
                                                            countryCode,
                                                            province: "",
                                                        }));
                                                    }}
                                                    placeholder="Select country..."
                                                    isSearchable
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        control: (
                                                            provided,
                                                            state
                                                        ) => ({
                                                            ...provided,
                                                            borderRadius:
                                                                "9999px",
                                                            borderColor:
                                                                state.isFocused
                                                                    ? "#9ca3af"
                                                                    : "#d1d5db",
                                                            boxShadow:
                                                                state.isFocused
                                                                    ? "0 0 0 1px #9ca3af"
                                                                    : "none",
                                                            "&:hover": {
                                                                borderColor:
                                                                    "#9ca3af",
                                                            },
                                                        }),
                                                    }}
                                                />
                                                {isCustomCountry && (
                                                    <input
                                                        className="mt-2 rounded-full border border-gray-300 px-4 py-2 w-full"
                                                        placeholder="Enter country"
                                                        value={
                                                            newJobForm.country
                                                        }
                                                        onChange={(e) =>
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    country:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Province/State
                                                </label>
                                                {newJobForm.countryCode ? (
                                                    <Select
                                                        options={[
                                                            {
                                                                value: "__NA__",
                                                                label: "N/A",
                                                            },
                                                            {
                                                                value: "__OTHER__",
                                                                label: "Other",
                                                            },
                                                            ...State.getStatesOfCountry(
                                                                newJobForm.countryCode
                                                            ).map((s) => ({
                                                                value: s.isoCode,
                                                                label: s.name,
                                                            })),
                                                        ]}
                                                        value={
                                                            newJobForm.province ===
                                                            "__NA__"
                                                                ? {
                                                                      value: "__NA__",
                                                                      label: "N/A",
                                                                  }
                                                                : isCustomProvince
                                                                ? {
                                                                      value: "__OTHER__",
                                                                      label: "Other",
                                                                  }
                                                                : newJobForm.province
                                                                ? {
                                                                      value: newJobForm.province,
                                                                      label: newJobForm.province,
                                                                  }
                                                                : null
                                                        }
                                                        onChange={(
                                                            selectedOption
                                                        ) => {
                                                            if (
                                                                !selectedOption
                                                            ) {
                                                                setIsCustomProvince(
                                                                    false
                                                                );
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        province:
                                                                            "",
                                                                    })
                                                                );
                                                                return;
                                                            }
                                                            if (
                                                                selectedOption.value ===
                                                                "__NA__"
                                                            ) {
                                                                setIsCustomProvince(
                                                                    false
                                                                );
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        province:
                                                                            "__NA__",
                                                                    })
                                                                );
                                                                return;
                                                            }
                                                            if (
                                                                selectedOption.value ===
                                                                "__OTHER__"
                                                            ) {
                                                                setIsCustomProvince(
                                                                    true
                                                                );
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        province:
                                                                            "",
                                                                    })
                                                                );
                                                                return;
                                                            }
                                                            setIsCustomProvince(
                                                                false
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    province:
                                                                        selectedOption.label ||
                                                                        "",
                                                                })
                                                            );
                                                        }}
                                                        placeholder="Select province/state..."
                                                        isSearchable
                                                        classNamePrefix="react-select"
                                                        styles={{
                                                            control: (
                                                                provided,
                                                                state
                                                            ) => ({
                                                                ...provided,
                                                                borderRadius:
                                                                    "9999px",
                                                                borderColor:
                                                                    state.isFocused
                                                                        ? "#9ca3af"
                                                                        : "#d1d5db",
                                                                boxShadow:
                                                                    state.isFocused
                                                                        ? "0 0 0 1px #9ca3af"
                                                                        : "none",
                                                                "&:hover": {
                                                                    borderColor:
                                                                        "#9ca3af",
                                                                },
                                                            }),
                                                        }}
                                                    />
                                                ) : (
                                                    <input
                                                        type="text"
                                                        className="rounded-full border border-gray-300 px-4 py-2 w-full bg-gray-50"
                                                        value={
                                                            newJobForm.province
                                                        }
                                                        onChange={(e) =>
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    province:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            )
                                                        }
                                                        placeholder="Select country first"
                                                        disabled
                                                    />
                                                )}
                                                {newJobForm.countryCode &&
                                                    isCustomProvince && (
                                                        <input
                                                            className="mt-2 rounded-full border border-gray-300 px-4 py-2 w-full"
                                                            placeholder="Enter province/state"
                                                            value={
                                                                newJobForm.province
                                                            }
                                                            onChange={(e) =>
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        province:
                                                                            e
                                                                                .target
                                                                                .value,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                    )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Language requirement*
                                                </label>
                                                <Select
                                                    isMulti
                                                    options={languages.map(
                                                        (lang) => ({
                                                            value: lang,
                                                            label: lang,
                                                        })
                                                    )}
                                                    value={
                                                        Array.isArray(
                                                            newJobForm.languageRequirement
                                                        )
                                                            ? newJobForm.languageRequirement.map(
                                                                  (lang) => ({
                                                                      value: lang,
                                                                      label: lang,
                                                                  })
                                                              )
                                                            : []
                                                    }
                                                    onChange={(
                                                        selectedOptions
                                                    ) => {
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            languageRequirement:
                                                                selectedOptions
                                                                    ? selectedOptions.map(
                                                                          (
                                                                              opt
                                                                          ) =>
                                                                              opt.value
                                                                      )
                                                                    : [],
                                                        }));
                                                    }}
                                                    placeholder="Select language(s)..."
                                                    isSearchable
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        control: (
                                                            provided,
                                                            state
                                                        ) => ({
                                                            ...provided,
                                                            borderRadius:
                                                                "9999px",
                                                            borderColor:
                                                                state.isFocused
                                                                    ? "#9ca3af"
                                                                    : "#d1d5db",
                                                            minHeight: "42px",
                                                            "&:hover": {
                                                                borderColor:
                                                                    "#9ca3af",
                                                            },
                                                        }),
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Location or licence
                                                        restrictions*
                                                    </label>
                                                </div>
                                                <div className="flex gap-2 mb-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const answer =
                                                                newJobForm.locationOrLicenceRestrictions ===
                                                                "yes"
                                                                    ? null
                                                                    : "yes";
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    locationOrLicenceRestrictions:
                                                                        answer,
                                                                    locationOrLicenceRestrictionsText:
                                                                        answer ===
                                                                        null
                                                                            ? ""
                                                                            : p.locationOrLicenceRestrictionsText,
                                                                })
                                                            );
                                                        }}
                                                        className={`flex-1 flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                            newJobForm.locationOrLicenceRestrictions ===
                                                            "yes"
                                                                ? "bg-gray-800 text-white border-gray-800"
                                                                : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                        }`}
                                                    >
                                                        Yes
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const answer =
                                                                newJobForm.locationOrLicenceRestrictions ===
                                                                "no"
                                                                    ? null
                                                                    : "no";
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    locationOrLicenceRestrictions:
                                                                        answer,
                                                                    locationOrLicenceRestrictionsText:
                                                                        answer ===
                                                                        null
                                                                            ? ""
                                                                            : p.locationOrLicenceRestrictionsText,
                                                                })
                                                            );
                                                        }}
                                                        className={`flex-1 flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                            newJobForm.locationOrLicenceRestrictions ===
                                                            "no"
                                                                ? "bg-gray-800 text-white border-gray-800"
                                                                : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                        }`}
                                                    >
                                                        No
                                                    </button>
                                                </div>
                                                {newJobForm.locationOrLicenceRestrictions ===
                                                    "yes" && (
                                                    <div>
                                                        <label className="block text-sm text-gray-700 mb-1">
                                                            What are the
                                                            restrictions
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                                            value={
                                                                newJobForm.locationOrLicenceRestrictionsText
                                                            }
                                                            onChange={(e) =>
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        locationOrLicenceRestrictionsText:
                                                                            e
                                                                                .target
                                                                                .value,
                                                                    })
                                                                )
                                                            }
                                                            placeholder="Enter restrictions"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 3: Requirements */}
                                {jobFormStep === 3 && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Specialty
                                                </label>
                                                <select
                                                    className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                                    value={
                                                        isCustomRequirementsSpecialty
                                                            ? "__OTHER__"
                                                            : newJobForm.requirementsSpecialty ||
                                                              ""
                                                    }
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        if (
                                                            value ===
                                                            "__OTHER__"
                                                        ) {
                                                            setIsCustomRequirementsSpecialty(
                                                                true
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    requirementsSpecialty:
                                                                        "",
                                                                })
                                                            );
                                                            return;
                                                        }
                                                        setIsCustomRequirementsSpecialty(
                                                            false
                                                        );
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            requirementsSpecialty:
                                                                value,
                                                        }));
                                                    }}
                                                >
                                                    <option value="">
                                                        Select a specialty
                                                    </option>
                                                    {Array.isArray(
                                                        specialties
                                                    ) &&
                                                        specialties.map((s) => (
                                                            <option
                                                                key={s}
                                                                value={s}
                                                            >
                                                                {s}
                                                            </option>
                                                        ))}
                                                    <option value="__OTHER__">
                                                        Other
                                                    </option>
                                                </select>
                                                {isCustomRequirementsSpecialty && (
                                                    <input
                                                        className="mt-2 rounded-full border border-gray-300 px-4 py-2 w-full"
                                                        placeholder="Enter specialty"
                                                        value={
                                                            newJobForm.requirementsSpecialty
                                                        }
                                                        onChange={(e) =>
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    requirementsSpecialty:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Experience level eg. Grade
                                                </label>
                                                <select
                                                    className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                                    value={
                                                        isCustomExperienceLevel
                                                            ? "__OTHER__"
                                                            : newJobForm.experienceLevel ||
                                                              ""
                                                    }
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        if (
                                                            value ===
                                                            "__OTHER__"
                                                        ) {
                                                            setIsCustomExperienceLevel(
                                                                true
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    experienceLevel:
                                                                        "",
                                                                })
                                                            );
                                                            return;
                                                        }
                                                        setIsCustomExperienceLevel(
                                                            false
                                                        );
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            experienceLevel:
                                                                value,
                                                        }));
                                                    }}
                                                >
                                                    <option value="">
                                                        Select experience level
                                                    </option>
                                                    <option value="medical-student">
                                                        Medical Student
                                                    </option>
                                                    <option value="junior-doctor">
                                                        Junior Doctor (Intern)
                                                    </option>
                                                    <option value="medical-officer">
                                                        Medical Officer
                                                    </option>
                                                    <option value="registrar">
                                                        Registrar
                                                    </option>
                                                    <option value="fellow">
                                                        Fellow
                                                    </option>
                                                    <option value="specialist">
                                                        Specialist
                                                    </option>
                                                    <option value="consultant">
                                                        Consultant
                                                    </option>
                                                    <option value="__OTHER__">
                                                        Other
                                                    </option>
                                                </select>
                                                {isCustomExperienceLevel && (
                                                    <input
                                                        className="mt-2 rounded-full border border-gray-300 px-4 py-2 w-full"
                                                        placeholder="Enter experience level"
                                                        value={
                                                            newJobForm.experienceLevel
                                                        }
                                                        onChange={(e) =>
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    experienceLevel:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Budget for role
                                                </label>
                                                <div className="flex gap-1">
                                                    <select
                                                        className="rounded-full border border-gray-300 px-2 py-2 bg-gray-50 text-sm w-[75px]"
                                                        value={
                                                            isCustomSalaryCurrency
                                                                ? "__OTHER__"
                                                                : newJobForm.salaryCurrency ||
                                                                  "ZAR"
                                                        }
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            if (
                                                                value ===
                                                                "__OTHER__"
                                                            ) {
                                                                setIsCustomSalaryCurrency(
                                                                    true
                                                                );
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        salaryCurrency:
                                                                            "",
                                                                    })
                                                                );
                                                                return;
                                                            }
                                                            setIsCustomSalaryCurrency(
                                                                false
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    salaryCurrency:
                                                                        value,
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        {currencies.map(
                                                            (currency) => (
                                                                <option
                                                                    key={
                                                                        currency.code
                                                                    }
                                                                    value={
                                                                        currency.code
                                                                    }
                                                                >
                                                                    {
                                                                        currency.code
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                        <option value="__OTHER__">
                                                            Other
                                                        </option>
                                                    </select>
                                                    {isCustomSalaryCurrency && (
                                                        <input
                                                            className="rounded-full border border-gray-300 px-3 py-2 w-[110px]"
                                                            placeholder="Code"
                                                            value={
                                                                newJobForm.salaryCurrency
                                                            }
                                                            onChange={(e) =>
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        salaryCurrency:
                                                                            e
                                                                                .target
                                                                                .value,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        className="rounded-full border border-gray-300 px-3 py-2 flex-1 min-w-0"
                                                        value={
                                                            newJobForm.budgetForRole
                                                        }
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value.replace(
                                                                    /[^0-9\s,\-–]/g,
                                                                    ""
                                                                );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    budgetForRole:
                                                                        value,
                                                                })
                                                            );
                                                        }}
                                                        placeholder="Range"
                                                    />
                                                    <select
                                                        className="rounded-full border border-gray-300 px-2 py-2 bg-gray-50 text-sm w-[85px]"
                                                        value={
                                                            isCustomSalaryPeriod
                                                                ? "__OTHER__"
                                                                : newJobForm.salaryPeriod ||
                                                                  "yearly"
                                                        }
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            if (
                                                                value ===
                                                                "__OTHER__"
                                                            ) {
                                                                setIsCustomSalaryPeriod(
                                                                    true
                                                                );
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        salaryPeriod:
                                                                            "",
                                                                    })
                                                                );
                                                                return;
                                                            }
                                                            setIsCustomSalaryPeriod(
                                                                false
                                                            );
                                                            setNewJobForm(
                                                                (p) => ({
                                                                    ...p,
                                                                    salaryPeriod:
                                                                        value,
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        {salaryPeriods.map(
                                                            (period) => (
                                                                <option
                                                                    key={
                                                                        period.value
                                                                    }
                                                                    value={
                                                                        period.value
                                                                    }
                                                                >
                                                                    {
                                                                        period.label
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                        <option value="__OTHER__">
                                                            Other
                                                        </option>
                                                    </select>
                                                    {isCustomSalaryPeriod && (
                                                        <input
                                                            className="rounded-full border border-gray-300 px-3 py-2 w-[110px]"
                                                            placeholder="Period"
                                                            value={
                                                                newJobForm.salaryPeriod
                                                            }
                                                            onChange={(e) =>
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        salaryPeriod:
                                                                            e
                                                                                .target
                                                                                .value,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-700 mb-1">
                                                    Years of Experience
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="rounded-full border border-gray-300 px-4 py-2 w-full"
                                                    value={
                                                        newJobForm.yearsOfExperience
                                                    }
                                                    onChange={(e) =>
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            yearsOfExperience:
                                                                e.target.value,
                                                        }))
                                                    }
                                                    placeholder="e.g. 3"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">
                                                What skills are required?
                                            </label>
                                            <div className="relative">
                                                <input
                                                    className="rounded-full border border-gray-300 px-4 py-2 w-full pr-10"
                                                    value={skillInput}
                                                    onChange={(e) =>
                                                        setSkillInput(
                                                            e.target.value
                                                        )
                                                    }
                                                    onKeyPress={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            addSkill(
                                                                skillInput
                                                            );
                                                        }
                                                    }}
                                                    placeholder="Enter skill and press Enter"
                                                />
                                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        className="w-5 h-5 text-gray-400"
                                                    >
                                                        <circle
                                                            cx="11"
                                                            cy="11"
                                                            r="8"
                                                        />
                                                        <path d="m21 21-4.35-4.35" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                'Enter' to add Skills
                                            </p>
                                            {newJobForm.requiredSkills.length >
                                                0 && (
                                                <div className="mt-3">
                                                    <div className="flex flex-wrap gap-2">
                                                        {newJobForm.requiredSkills.map(
                                                            (skill, index) => (
                                                                <span
                                                                    key={index}
                                                                    className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm"
                                                                >
                                                                    {skill}
                                                                    <button
                                                                        onClick={() =>
                                                                            removeSkill(
                                                                                index
                                                                            )
                                                                        }
                                                                        className="text-gray-500 hover:text-red-600"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </span>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-3">
                                                Other preferences
                                            </label>
                                            <div className="flex flex-wrap gap-2 mb-4 enhanced-hiring-chip-row">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            screeningQuestions:
                                                                !p.screeningQuestions,
                                                        }))
                                                    }
                                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                        newJobForm.screeningQuestions
                                                            ? "bg-black text-white border-black"
                                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 4v16m8-8H4"
                                                        />
                                                    </svg>
                                                    Screening questions
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newValue =
                                                            !newJobForm.driversLicenseRequired;
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            driversLicenseRequired:
                                                                newValue,
                                                        }));
                                                        if (!newValue)
                                                            setDriversLicenseAnswer(
                                                                null
                                                            );
                                                    }}
                                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                        newJobForm.driversLicenseRequired
                                                            ? "bg-black text-white border-black"
                                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 4v16m8-8H4"
                                                        />
                                                    </svg>
                                                    Drivers license required
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newValue =
                                                            !newJobForm.visaSponsorshipOffered;
                                                        setNewJobForm((p) => ({
                                                            ...p,
                                                            visaSponsorshipOffered:
                                                                newValue,
                                                        }));
                                                        if (!newValue)
                                                            setVisaSponsorshipAnswer(
                                                                null
                                                            );
                                                    }}
                                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                        newJobForm.visaSponsorshipOffered
                                                            ? "bg-black text-white border-black"
                                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 4v16m8-8H4"
                                                        />
                                                    </svg>
                                                    Visa sponsorship offered
                                                </button>
                                            </div>
                                            {screeningQuestionsList.length >
                                                0 && (
                                                <div className="mb-4 space-y-3">
                                                    <div className="text-sm font-medium text-gray-700 mb-2">
                                                        Saved Questions:
                                                    </div>
                                                    {screeningQuestionsList.map(
                                                        (
                                                            savedQuestion,
                                                            qIndex
                                                        ) => (
                                                            <div
                                                                key={qIndex}
                                                                className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                                                            >
                                                                <div className="flex items-start justify-between mb-2">
                                                                    <p className="text-sm font-medium text-gray-900">
                                                                        {
                                                                            savedQuestion.question
                                                                        }
                                                                    </p>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            removeScreeningQuestion(
                                                                                qIndex
                                                                            )
                                                                        }
                                                                        className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 ml-2"
                                                                    >
                                                                        <svg
                                                                            className="w-5 h-5"
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                            viewBox="0 0 24 24"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                d="M6 18L18 6M6 6l12 12"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                                {savedQuestion.type ===
                                                                    "multiple-choice" &&
                                                                    savedQuestion.options &&
                                                                    Array.isArray(
                                                                        savedQuestion.options
                                                                    ) && (
                                                                        <div className="space-y-1 mt-2">
                                                                            {savedQuestion.options.map(
                                                                                (
                                                                                    option,
                                                                                    optIndex
                                                                                ) => (
                                                                                    <div
                                                                                        key={
                                                                                            optIndex
                                                                                        }
                                                                                        className="flex items-center gap-2 text-sm text-gray-600"
                                                                                    >
                                                                                        <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-600 flex-shrink-0">
                                                                                            {String.fromCharCode(
                                                                                                9312 +
                                                                                                    optIndex
                                                                                            )}
                                                                                        </div>
                                                                                        <span>
                                                                                            {
                                                                                                option
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                )
                                                                            )}
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                            {newJobForm.screeningQuestions && (
                                                <div className="mb-4 space-y-3">
                                                    <div>
                                                        <input
                                                            type="text"
                                                            placeholder="Type your question"
                                                            value={
                                                                currentQuestion
                                                            }
                                                            onChange={(e) =>
                                                                setCurrentQuestion(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className="w-full rounded-xl border border-gray-300 px-4 py-2 text-sm"
                                                        />
                                                    </div>

                                                    {/* Question Type Buttons - matching image style */}
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setCurrentQuestionType(
                                                                    "multiple-choice"
                                                                )
                                                            }
                                                            className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                                currentQuestionType ===
                                                                "multiple-choice"
                                                                    ? "bg-gray-900 text-white border-gray-900"
                                                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                                            }`}
                                                        >
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M12 4v16m8-8H4"
                                                                />
                                                            </svg>
                                                            Multi-choice
                                                            question
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setCurrentQuestionType(
                                                                    "text"
                                                                )
                                                            }
                                                            className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                                currentQuestionType ===
                                                                "text"
                                                                    ? "bg-gray-900 text-white border-gray-900"
                                                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                                            }`}
                                                        >
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M12 4v16m8-8H4"
                                                                />
                                                            </svg>
                                                            Long answer
                                                        </button>
                                                    </div>

                                                    {/* Options input - only show for multi-choice */}
                                                    {currentQuestionType ===
                                                        "multiple-choice" && (
                                                        <>
                                                            <div className="space-y-2">
                                                                {currentOptions.map(
                                                                    (
                                                                        option,
                                                                        index
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                index
                                                                            }
                                                                            className="flex items-center gap-2"
                                                                        >
                                                                            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs text-gray-600 flex-shrink-0">
                                                                                {String.fromCharCode(
                                                                                    9312 +
                                                                                        index
                                                                                )}
                                                                            </div>
                                                                            <input
                                                                                type="text"
                                                                                placeholder={`Option ${
                                                                                    index +
                                                                                    1
                                                                                }`}
                                                                                value={
                                                                                    option
                                                                                }
                                                                                onChange={(
                                                                                    e
                                                                                ) =>
                                                                                    updateScreeningOption(
                                                                                        index,
                                                                                        e
                                                                                            .target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                                className="flex-1 rounded-xl border border-gray-300 px-4 py-2 text-sm bg-gray-50"
                                                                            />
                                                                            {currentOptions.length >
                                                                                1 && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() =>
                                                                                        removeScreeningOption(
                                                                                            index
                                                                                        )
                                                                                    }
                                                                                    className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                                                                >
                                                                                    <svg
                                                                                        className="w-5 h-5"
                                                                                        fill="none"
                                                                                        stroke="currentColor"
                                                                                        viewBox="0 0 24 24"
                                                                                    >
                                                                                        <path
                                                                                            strokeLinecap="round"
                                                                                            strokeLinejoin="round"
                                                                                            strokeWidth={
                                                                                                2
                                                                                            }
                                                                                            d="M6 18L18 6M6 6l12 12"
                                                                                        />
                                                                                    </svg>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={
                                                                    addScreeningOption
                                                                }
                                                                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                                            >
                                                                <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center">
                                                                    <svg
                                                                        className="w-4 h-4"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={
                                                                                2
                                                                            }
                                                                            d="M12 4v16m8-8H4"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                                Add option
                                                            </button>
                                                        </>
                                                    )}

                                                    {/* Preview textarea for long answer */}
                                                    {currentQuestionType ===
                                                        "text" && (
                                                        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                                                            <p className="text-xs text-gray-500 mb-2">
                                                                Preview
                                                            </p>
                                                            <textarea
                                                                disabled
                                                                placeholder="Type your answer here..."
                                                                rows={4}
                                                                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm bg-white resize-y cursor-not-allowed opacity-75"
                                                            />
                                                        </div>
                                                    )}

                                                    <button
                                                        type="button"
                                                        onClick={
                                                            addScreeningQuestion
                                                        }
                                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors"
                                                    >
                                                        <svg
                                                            className="w-4 h-4"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M12 4v16m8-8H4"
                                                            />
                                                        </svg>
                                                        Add question
                                                    </button>
                                                </div>
                                            )}
                                            {newJobForm.driversLicenseRequired && (
                                                <div className="mb-4 space-y-3">
                                                    <div className="text-sm font-medium text-gray-900 mb-3">
                                                        Do you have a driver's
                                                        licence?
                                                    </div>
                                                    <div className="space-y-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const answer =
                                                                    driversLicenseAnswer ===
                                                                    "yes"
                                                                        ? null
                                                                        : "yes";
                                                                setDriversLicenseAnswer(
                                                                    answer
                                                                );
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        driversLicenseAnswer:
                                                                            answer,
                                                                    })
                                                                );
                                                            }}
                                                            className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                                driversLicenseAnswer ===
                                                                "yes"
                                                                    ? "bg-black text-white border-black"
                                                                    : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                            }`}
                                                        >
                                                            Yes
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const answer =
                                                                    driversLicenseAnswer ===
                                                                    "no"
                                                                        ? null
                                                                        : "no";
                                                                setDriversLicenseAnswer(
                                                                    answer
                                                                );
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        driversLicenseAnswer:
                                                                            answer,
                                                                    })
                                                                );
                                                            }}
                                                            className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                                driversLicenseAnswer ===
                                                                "no"
                                                                    ? "bg-black text-white border-black"
                                                                    : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                            }`}
                                                        >
                                                            No
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {newJobForm.visaSponsorshipOffered && (
                                                <div className="mb-4 space-y-3">
                                                    <div className="text-sm font-medium text-gray-900 mb-3">
                                                        Do you need a visa
                                                        sponsorship?
                                                    </div>
                                                    <div className="space-y-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const answer =
                                                                    visaSponsorshipAnswer ===
                                                                    "yes"
                                                                        ? null
                                                                        : "yes";
                                                                setVisaSponsorshipAnswer(
                                                                    answer
                                                                );
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        visaSponsorshipAnswer:
                                                                            answer,
                                                                    })
                                                                );
                                                            }}
                                                            className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                                visaSponsorshipAnswer ===
                                                                "yes"
                                                                    ? "bg-black text-white border-black"
                                                                    : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                            }`}
                                                        >
                                                            Yes
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const answer =
                                                                    visaSponsorshipAnswer ===
                                                                    "no"
                                                                        ? null
                                                                        : "no";
                                                                setVisaSponsorshipAnswer(
                                                                    answer
                                                                );
                                                                setNewJobForm(
                                                                    (p) => ({
                                                                        ...p,
                                                                        visaSponsorshipAnswer:
                                                                            answer,
                                                                    })
                                                                );
                                                            }}
                                                            className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                                visaSponsorshipAnswer ===
                                                                "no"
                                                                    ? "bg-black text-white border-black"
                                                                    : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                                            }`}
                                                        >
                                                            No
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            <textarea
                                                rows={3}
                                                className="w-full rounded-2xl border border-gray-200 px-4 py-3"
                                                placeholder="Any other preferences or requirements..."
                                                value={
                                                    newJobForm.otherPreferences
                                                }
                                                onChange={(e) =>
                                                    setNewJobForm((p) => ({
                                                        ...p,
                                                        otherPreferences:
                                                            e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="px-6 py-4 border-t flex items-center gap-3 enhanced-hiring-modal-footer">
                                <button
                                    onClick={attemptCloseNewJobModal}
                                    className="px-5 py-2 rounded-full border text-sm"
                                >
                                    Cancel
                                </button>
                                {jobFormStep === 2 ? (
                                    <button
                                        onClick={handleNextStep}
                                        className="ml-auto px-6 py-2 rounded-full bg-black text-white text-sm"
                                    >
                                        Next step
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            onClick={handleBackStep}
                                            className="px-5 py-2 rounded-full border text-sm"
                                        >
                                            Back
                                        </button>
                                        <button
                                            onClick={createJobListing}
                                            disabled={savingNewJob}
                                            className="ml-auto px-6 py-2 rounded-full bg-black text-white text-sm disabled:opacity-60"
                                        >
                                            {savingNewJob
                                                ? "Saving…"
                                                : "Create job"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {showNewJobLeaveWarning && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
                        onClick={dismissNewJobLeaveWarning}
                        role="presentation"
                    >
                        <div
                            className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
                            onClick={(e) => e.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="enhanced-hiring-new-job-leave-warning-title"
                            aria-describedby="enhanced-hiring-new-job-leave-warning-desc"
                        >
                            <h2
                                id="enhanced-hiring-new-job-leave-warning-title"
                                className="text-2xl font-bold mb-4 text-black"
                            >
                                Leave without saving?
                            </h2>
                            <p
                                id="enhanced-hiring-new-job-leave-warning-desc"
                                className="text-black mb-6 leading-relaxed"
                            >
                                {NEW_JOB_LEAVE_WARNING_MESSAGE}
                            </p>
                            <div className="flex justify-end gap-4">
                                <button
                                    type="button"
                                    className="px-4 py-2 text-black cursor-pointer"
                                    onClick={dismissNewJobLeaveWarning}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 cursor-pointer"
                                    onClick={confirmLeaveNewJobModal}
                                >
                                    Leave
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default EnhancedHiringPipeline;
